function escapeHtml(value) {
  const element = document.createElement('div');
  element.textContent = String(value ?? '');
  return element.innerHTML;
}

function shopifyRoute(path) {
  const root = window.Shopify?.routes?.root || '/';
  return `${root.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`;
}

function formatCartMoney(cents, currency = 'USD') {
  return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency,
  }).format((Number(cents) || 0) / 100);
}

function formatProductCardMoney(cents, currency = 'USD') {
  return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format((Number(cents) || 0) / 100);
}

function updateCartCounts(count) {
  document.querySelectorAll('[data-cart-count]').forEach((badge) => {
    badge.textContent = count;
    badge.hidden = count === 0;
    badge
      .closest('a')
      ?.setAttribute(
        'aria-label',
        `Bag, ${count} ${count === 1 ? 'item' : 'items'}`,
      );
  });

  document.querySelectorAll('[data-cart-drawer-count]').forEach((value) => {
    value.textContent = count;
  });
}

function getVariantPricing(variantId) {
  const normalizedId = String(variantId);

  for (const select of document.querySelectorAll(
    '[data-product-variant-select]',
  )) {
    const option = [...select.options].find(
      (candidate) => candidate.value === normalizedId,
    );
    if (!option) continue;

    return {
      price: Number(option.dataset.price) || 0,
      compareAtPrice: Number(option.dataset.compareAt) || 0,
    };
  }

  for (const script of document.querySelectorAll('[data-product-variants]')) {
    try {
      const variant = JSON.parse(script.textContent || '[]').find(
        (candidate) => String(candidate.id) === normalizedId,
      );
      if (!variant) continue;

      return {
        price: Number(variant.price) || 0,
        compareAtPrice: Number(variant.compareAtPrice) || 0,
      };
    } catch {
      // Ignore malformed product data and let the cart API price take over.
    }
  }

  return null;
}

function renderCartDrawerItem(item, currency) {
  const image = item.featured_image?.url || item.image || '';
  const title = item.product_title || item.title || 'Product';
  const variant =
    item.variant_title && item.variant_title !== 'Default Title'
      ? item.variant_title
      : '';
  const price = Number(item.final_price ?? item.price) || 0;
  const compareAt = Math.max(
    Number(item.flb_compare_at_price) || 0,
    Number(item.compare_at_price) || 0,
    Number(item.original_price) || 0,
  );
  const hasDiscount = compareAt > price;
  const discount = hasDiscount
    ? Math.round(((compareAt - price) / compareAt) * 100)
    : 0;

  return `
    <a class="cart-drawer__image" href="${escapeHtml(item.url || '#')}">
      ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(title)}">` : ''}
    </a>
    <div class="cart-drawer__copy">
      ${hasDiscount ? `<span class="discount-badge cart-drawer__discount">${discount}% off</span>` : ''}
      <a href="${escapeHtml(item.url || '#')}"><h3>${escapeHtml(title)}</h3></a>
      ${variant ? `<p>${escapeHtml(variant)} &middot; Qty ${Number(item.quantity) || 1}</p>` : `<p>Qty ${Number(item.quantity) || 1}</p>`}
      <div class="cart-drawer__price${hasDiscount ? ' cart-drawer__price--sale' : ''}">
        ${hasDiscount ? `<s>${formatCartMoney(compareAt, currency)}</s>` : ''}
        <strong>${formatCartMoney(price, currency)}</strong>
      </div>
    </div>
  `;
}

export async function addShopifyCartItem(variantId, quantity = 1) {
  const drawer = document.querySelector('[data-cart-drawer]');
  const addUrl = drawer?.dataset.cartAddUrl || shopifyRoute('cart/add.js');
  const cartUrl = drawer?.dataset.cartUrl || shopifyRoute('cart.js');
  const variantPricing = getVariantPricing(variantId);
  const response = await fetch(addUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      items: [
        { id: Number(variantId), quantity: Math.max(1, Number(quantity) || 1) },
      ],
    }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      payload.description ||
        payload.message ||
        'This product could not be added to your bag.',
    );
  }

  const addedResponseItem = payload.items?.[0] || payload;
  const cartResponse = await fetch(cartUrl, {
    headers: { Accept: 'application/json' },
  });
  const cart = cartResponse.ok ? await cartResponse.json() : null;
  const cartItem =
    cart?.items?.find((item) => item.key === addedResponseItem.key) ||
    cart?.items?.find(
      (item) => Number(item.variant_id) === Number(variantId),
    ) ||
    addedResponseItem;
  const addedItem = variantPricing
    ? {
        ...cartItem,
        flb_compare_at_price: variantPricing.compareAtPrice,
      }
    : cartItem;

  document.dispatchEvent(
    new CustomEvent('flb:cart-change', {
      detail: { cart, added: addedItem },
    }),
  );

  return { cart, added: addedItem };
}

export function initCartUi() {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer || drawer.dataset.initialized === 'true') return;
  drawer.dataset.initialized = 'true';

  const drawerItem = drawer.querySelector('[data-cart-drawer-item]');
  const status = drawer.querySelector('[data-cart-status]');
  const currency = drawer.dataset.currency || 'USD';
  let closeTimer = null;
  let returnFocus = null;
  let lockedScrollY = null;
  let previousBodyTop = '';

  const syncDrawerPosition = () => {
    const top = [
      ...document.querySelectorAll('.announcement, .site-header'),
    ].reduce(
      (maximum, element) =>
        Math.max(maximum, element.getBoundingClientRect().bottom),
      0,
    );
    drawer.style.setProperty(
      '--cart-drawer-top',
      `${Math.max(0, Math.round(top))}px`,
    );
  };

  const lockPageScroll = () => {
    if (lockedScrollY !== null) return;
    lockedScrollY = window.scrollY;
    previousBodyTop = document.body.style.top;
    document.dispatchEvent(new CustomEvent('flb:scroll-lock'));
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.classList.add('cart-drawer-open');
  };

  const unlockPageScroll = () => {
    if (lockedScrollY === null) {
      document.body.classList.remove('cart-drawer-open');
      return;
    }
    const scrollY = lockedScrollY;
    lockedScrollY = null;
    const root = document.documentElement;
    const previousBehavior = root.style.getPropertyValue('scroll-behavior');
    const previousBehaviorPriority =
      root.style.getPropertyPriority('scroll-behavior');
    root.style.setProperty('scroll-behavior', 'auto', 'important');
    document.body.classList.remove('cart-drawer-open');
    document.body.style.top = previousBodyTop;
    window.scrollTo({ left: 0, top: scrollY, behavior: 'auto' });
    if (previousBehavior) {
      root.style.setProperty(
        'scroll-behavior',
        previousBehavior,
        previousBehaviorPriority,
      );
    } else root.style.removeProperty('scroll-behavior');
    document.dispatchEvent(
      new CustomEvent('flb:scroll-unlock', { detail: { scrollY } }),
    );
  };

  const closeDrawer = () => {
    if (drawer.hidden) return;
    drawer.classList.remove('is-open');
    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => {
      drawer.hidden = true;
      unlockPageScroll();
      try {
        returnFocus?.focus?.({ preventScroll: true });
      } catch {
        // Do not use a fallback focus call that can move the restored viewport.
      }
    }, 420);
  };

  const openDrawer = (item) => {
    if (!item || !drawerItem) return;
    window.clearTimeout(closeTimer);
    returnFocus = document.activeElement;
    drawerItem.innerHTML = renderCartDrawerItem(item, currency);
    if (status)
      status.textContent = `${item.product_title || item.title || 'Product'} added to bag.`;
    syncDrawerPosition();
    drawer.hidden = false;
    lockPageScroll();
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      drawer
        .querySelector('[data-cart-drawer-close]')
        ?.focus({ preventScroll: true });
    });
  };

  drawer
    .querySelectorAll('[data-cart-drawer-close], [data-cart-drawer-backdrop]')
    .forEach((control) => control.addEventListener('click', closeDrawer));

  drawer.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || drawer.hidden) return;
    const focusable = [
      ...drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !drawer.hidden) closeDrawer();
  });
  window.addEventListener('resize', () => {
    if (!drawer.hidden) syncDrawerPosition();
  });
  document.addEventListener('flb:cart-change', (event) => {
    if (event.detail?.cart) {
      updateCartCounts(Number(event.detail.cart.item_count) || 0);
    }
    if (event.detail?.added) openDrawer(event.detail.added);
  });
}

function renderCartPageLine(item, currency) {
  const image = item.featured_image?.url || item.image || '';
  const title = item.product_title || item.title || 'Product';
  const options = Array.isArray(item.options_with_values)
    ? item.options_with_values
        .map((option) => `${option.name}: ${option.value}`)
        .filter(Boolean)
    : Array.isArray(item.variant_options)
      ? item.variant_options.filter(
          (value) => value && value !== 'Default Title',
        )
      : [];
  const properties =
    item.properties && typeof item.properties === 'object'
      ? Object.entries(item.properties)
          .filter(([key, value]) => value && !key.startsWith('_'))
          .map(([key, value]) => `${key}: ${value}`)
      : [];
  const finalLinePrice = Number(item.final_line_price) || 0;
  const originalLinePrice = Number(item.original_line_price) || finalLinePrice;
  const sale = originalLinePrice > finalLinePrice;
  const discount = sale
    ? Math.round(
        ((originalLinePrice - finalLinePrice) / originalLinePrice) * 100,
      )
    : 0;
  const quantity = Number(item.quantity) || 1;
  const reduceAction = quantity === 1 ? 'remove' : 'decrease';
  const reduceLabel =
    quantity === 1 ? `Remove ${title}` : `Decrease quantity for ${title}`;
  const reduceIcon =
    quantity === 1
      ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14M10 11v6M14 11v6"/></svg>'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/></svg>';

  return `
    <article class="cart-line${sale ? ' cart-line--sale' : ''}" data-cart-line data-cart-key="${escapeHtml(item.key)}" data-cart-quantity="${quantity}">
      <a class="cart-line__image" href="${escapeHtml(item.url || '#')}">
        ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(title)}" loading="lazy">` : ''}
      </a>
      <div class="cart-line__content">
        ${sale ? `<span class="discount-badge cart-line__discount">-${discount}%</span>` : ''}
        <a href="${escapeHtml(item.url || '#')}"><h2>${escapeHtml(title)}</h2></a>
        ${[...options, ...properties].map((value) => `<p>${escapeHtml(value)}</p>`).join('')}
        <div class="quantity-stepper cart-line__quantity" aria-label="Quantity for ${escapeHtml(title)}">
          <button type="button" aria-label="${escapeHtml(reduceLabel)}" data-cart-line-action="${reduceAction}">${reduceIcon}</button>
          <span aria-live="polite" data-cart-line-quantity>${quantity}</span>
          <button type="button" aria-label="Increase quantity" data-cart-line-action="increase"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M5 12h14M12 5v14"/></svg></button>
        </div>
      </div>
      <div class="cart-line__price${sale ? ' cart-line__price--sale' : ''}">
        ${sale ? `<s>${formatCartMoney(originalLinePrice, currency)}</s>` : ''}
        <strong>${formatCartMoney(finalLinePrice, currency)}</strong>
      </div>
    </article>
  `;
}

export function initCartPage() {
  document.querySelectorAll('[data-cart-page]').forEach((page) => {
    if (page.dataset.cartPageInitialized === 'true') return;
    page.dataset.cartPageInitialized = 'true';
    page.closest('main')?.classList.add('main--cart');

    const cartUrl = page.dataset.cartUrl || shopifyRoute('cart.js');
    const changeUrl =
      page.dataset.cartChangeUrl || shopifyRoute('cart/change.js');
    const updateUrl =
      page.dataset.cartUpdateUrl || shopifyRoute('cart/update.js');
    const currency = page.dataset.currency || 'USD';
    const threshold = Number(page.dataset.shippingThreshold) || 0;
    const items = page.querySelector('[data-cart-items]');
    const empty = page.querySelector('[data-cart-empty]');
    const title = page.querySelector('[data-cart-page-title]');
    const status = page.querySelector('[data-cart-page-status]');
    const checkout = page.querySelector('[data-cart-checkout-button]');
    let currentCart = null;
    let updating = false;

    const render = (cart) => {
      if (!cart) return;
      currentCart = cart;
      const count = Number(cart.item_count) || 0;
      const subtotal = Number(cart.total_price) || 0;
      const originalSubtotal = Number(cart.original_total_price) || subtotal;
      const savings = Math.max(
        Number(cart.total_discount) || 0,
        originalSubtotal - subtotal,
      );
      const qualifies = count > 0 && subtotal >= threshold;

      page.classList.toggle('cart-page--empty', count === 0);
      if (title) title.textContent = count ? `Bag (${count})` : 'Your bag';
      if (empty) empty.hidden = count > 0;
      if (items) {
        items.hidden = count === 0;
        items.innerHTML = cart.items
          .map((item) => renderCartPageLine(item, currency))
          .join('');
      }

      const subtotalNode = page.querySelector('[data-cart-subtotal]');
      if (subtotalNode)
        subtotalNode.textContent = formatCartMoney(subtotal, currency);
      const subtotalCompare = page.querySelector(
        '[data-cart-subtotal-compare]',
      );
      if (subtotalCompare) {
        subtotalCompare.hidden = !savings;
        subtotalCompare.textContent = savings
          ? formatCartMoney(originalSubtotal, currency)
          : '';
      }
      const savingsRow = page.querySelector('[data-cart-savings-row]');
      if (savingsRow) savingsRow.hidden = !savings;
      const savingsNode = page.querySelector('[data-cart-savings]');
      if (savingsNode)
        savingsNode.textContent = formatCartMoney(savings, currency);
      const discountApplications = [
        ...(Array.isArray(cart.cart_level_discount_applications)
          ? cart.cart_level_discount_applications
          : []),
        ...(Array.isArray(cart.items)
          ? cart.items.flatMap((item) =>
              Array.isArray(item.line_level_discount_allocations)
                ? item.line_level_discount_allocations.map(
                    (allocation) => allocation.discount_application,
                  )
                : [],
            )
          : []),
      ];
      const hasDiscountCode =
        (Array.isArray(cart.discount_codes) &&
          cart.discount_codes.some(
            (discount) => discount.applicable !== false,
          )) ||
        discountApplications.some(
          (application) => application?.type === 'discount_code',
        );
      const removeDiscount = page.querySelector('[data-cart-remove-discount]');
      if (removeDiscount) removeDiscount.hidden = !hasDiscountCode;
      const shipping = page.querySelector('[data-cart-shipping]');
      if (shipping)
        shipping.textContent = !count ? '—' : qualifies ? 'Free' : '—';
      const total = page.querySelector('[data-cart-total]');
      if (total) total.textContent = formatCartMoney(subtotal, currency);
      const shippingMessage = page.querySelector(
        '[data-cart-shipping-message]',
      );
      if (shippingMessage) {
        shippingMessage.textContent = !count
          ? 'Add an item to view shipping options.'
          : qualifies
            ? 'Congrats! You get free standard shipping.'
            : `Add ${formatCartMoney(Math.max(0, threshold - subtotal), currency)} more to qualify for free shipping.`;
      }
      if (checkout) checkout.disabled = count === 0;
      updateCartCounts(count);
    };

    const changeLine = async (key, quantity) => {
      if (updating) return;
      updating = true;
      page.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(changeUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }),
        });
        const cart = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            cart.description ||
              cart.message ||
              'The cart could not be updated.',
          );
        }
        render(cart);
        if (status) status.textContent = 'Your bag was updated.';
        document.dispatchEvent(
          new CustomEvent('flb:cart-change', { detail: { cart } }),
        );
      } catch (error) {
        if (status) {
          status.textContent =
            error instanceof Error
              ? error.message
              : 'The cart could not be updated.';
        }
      } finally {
        updating = false;
        page.removeAttribute('aria-busy');
      }
    };

    page.addEventListener('click', (event) => {
      const action = event.target.closest('[data-cart-line-action]');
      if (!action || updating) return;
      const line = action.closest('[data-cart-line]');
      const key = line?.dataset.cartKey;
      const quantity = Number(line?.dataset.cartQuantity) || 1;
      if (!key) return;
      if (action.dataset.cartLineAction === 'increase')
        changeLine(key, quantity + 1);
      if (action.dataset.cartLineAction === 'decrease')
        changeLine(key, quantity - 1);
      if (action.dataset.cartLineAction === 'remove') changeLine(key, 0);
    });

    const discountForm = page.querySelector('[data-cart-discount-form]');
    const discountInput = discountForm?.querySelector('[name="discount"]');
    const discountButton = discountForm?.querySelector('[type="submit"]');
    const removeDiscountButton = page.querySelector(
      '[data-cart-remove-discount]',
    );
    const discountStatus = page.querySelector('[data-cart-discount-status]');

    if (discountForm) {
      discountInput?.addEventListener('input', () => {
        discountInput.removeAttribute('aria-invalid');
        if (discountStatus) {
          discountStatus.textContent = '';
          discountStatus.classList.remove('is-success');
        }
      });

      discountForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const form = event.currentTarget;
        const code = new FormData(form).get('discount')?.toString().trim();
        if (!code) {
          discountInput?.setAttribute('aria-invalid', 'true');
          if (discountStatus) {
            discountStatus.textContent = 'Enter a discount code.';
            discountStatus.classList.remove('is-success');
          }
          return;
        }

        discountButton?.setAttribute('disabled', '');
        form.setAttribute('aria-busy', 'true');
        if (discountStatus) {
          discountStatus.textContent = 'Applying discount code…';
          discountStatus.classList.remove('is-success');
        }

        try {
          const response = await fetch(updateUrl, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ discount: code }),
          });
          const cart = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(
              cart.description ||
                cart.message ||
                'The discount code could not be applied.',
            );
          }

          const normalizedCode = code.toLowerCase();
          const submittedDiscount = Array.isArray(cart.discount_codes)
            ? cart.discount_codes.find(
                (discount) =>
                  String(discount.code || '').toLowerCase() === normalizedCode,
              )
            : null;
          const discountApplications = [
            ...(Array.isArray(cart.cart_level_discount_applications)
              ? cart.cart_level_discount_applications
              : []),
            ...(Array.isArray(cart.items)
              ? cart.items.flatMap((item) =>
                  Array.isArray(item.line_level_discount_allocations)
                    ? item.line_level_discount_allocations.map(
                        (allocation) => allocation.discount_application,
                      )
                    : [],
                )
              : []),
          ];
          const hasMatchingApplication = discountApplications.some(
            (application) =>
              application?.type === 'discount_code' &&
              String(application.title || '').toLowerCase() === normalizedCode,
          );
          const applied = submittedDiscount
            ? submittedDiscount.applicable !== false
            : hasMatchingApplication;

          render(cart);
          document.dispatchEvent(
            new CustomEvent('flb:cart-change', { detail: { cart } }),
          );
          discountInput?.setAttribute('aria-invalid', String(!applied));
          if (discountStatus) {
            discountStatus.textContent = applied
              ? `Discount code ${code} applied.`
              : 'This discount code is invalid or not applicable to your cart.';
            discountStatus.classList.toggle('is-success', applied);
          }
        } catch (error) {
          discountInput?.setAttribute('aria-invalid', 'true');
          if (discountStatus) {
            discountStatus.textContent =
              error instanceof Error
                ? error.message
                : 'The discount code could not be applied.';
            discountStatus.classList.remove('is-success');
          }
        } finally {
          discountButton?.removeAttribute('disabled');
          form.removeAttribute('aria-busy');
        }
      });
    }

    removeDiscountButton?.addEventListener('click', async () => {
      removeDiscountButton.disabled = true;
      discountButton?.setAttribute('disabled', '');
      page.setAttribute('aria-busy', 'true');
      if (discountStatus) {
        discountStatus.textContent = 'Removing discount code…';
        discountStatus.classList.remove('is-success');
      }

      try {
        const response = await fetch(updateUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ discount: '' }),
        });
        const cart = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            cart.description ||
              cart.message ||
              'The discount code could not be removed.',
          );
        }

        render(cart);
        if (discountInput) discountInput.value = '';
        discountInput?.removeAttribute('aria-invalid');
        if (discountStatus) {
          discountStatus.textContent = 'Discount code removed.';
          discountStatus.classList.add('is-success');
        }
        document.dispatchEvent(
          new CustomEvent('flb:cart-change', { detail: { cart } }),
        );
      } catch (error) {
        if (discountStatus) {
          discountStatus.textContent =
            error instanceof Error
              ? error.message
              : 'The discount code could not be removed.';
          discountStatus.classList.remove('is-success');
        }
      } finally {
        removeDiscountButton.disabled = false;
        discountButton?.removeAttribute('disabled');
        page.removeAttribute('aria-busy');
      }
    });

    document.addEventListener('flb:cart-change', (event) => {
      if (event.detail?.cart && event.detail.cart !== currentCart) {
        render(event.detail.cart);
      }
    });

    fetch(cartUrl, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then(render)
      .catch(() => {});
  });
}

function setMegaMenu(item, open, { switching = false } = {}) {
  const trigger = item?.querySelector('[data-menu-trigger]');
  const menu = item?.querySelector('[data-mega-menu]');
  if (!trigger || !menu) return;

  if (menu.hideTimer) {
    window.clearTimeout(menu.hideTimer);
    menu.hideTimer = null;
  }

  item.classList.toggle('is-open', open);
  trigger.setAttribute('aria-expanded', String(open));
  menu.setAttribute('aria-hidden', String(!open));

  if (open) {
    menu.hidden = false;
    menu.inert = false;
    menu.classList.toggle('is-switching', switching);
    menu.classList.remove('is-visible');
    void menu.offsetHeight;

    requestAnimationFrame(() => {
      if (trigger.getAttribute('aria-expanded') === 'true') {
        menu.classList.add('is-visible');
      }
    });
    return;
  }

  menu.inert = true;
  menu.classList.toggle('is-switching', switching);
  menu.classList.remove('is-visible');
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  menu.hideTimer = window.setTimeout(
    () => {
      if (trigger.getAttribute('aria-expanded') === 'false') {
        menu.hidden = true;
        menu.classList.remove('is-switching');
      }
      menu.hideTimer = null;
    },
    reducedMotion || switching ? 0 : 520,
  );
}

function closeMegaMenus(except = null) {
  document.querySelectorAll('[data-menu-item]').forEach((item) => {
    if (item !== except) setMegaMenu(item, false);
  });
}

function getUnderlineOrigin(event, link) {
  if (event.movementX < 0) return 'right';
  if (event.movementX > 0) return 'left';

  const bounds = link.getBoundingClientRect();
  return event.clientX < bounds.left + bounds.width / 2 ? 'left' : 'right';
}

export function initDirectionalUnderlines() {
  const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');

  document.querySelectorAll('.directional-link').forEach((link) => {
    link.addEventListener('pointerenter', (event) => {
      if (!hoverMedia.matches) return;
      link.style.setProperty(
        '--underline-origin',
        getUnderlineOrigin(event, link),
      );
      link.classList.add('is-underline-active');
    });

    link.addEventListener('pointerleave', (event) => {
      if (!hoverMedia.matches) return;
      const bounds = link.getBoundingClientRect();
      const exitOrigin =
        event.clientX < bounds.left + bounds.width / 2 ? 'left' : 'right';
      link.style.setProperty('--underline-origin', exitOrigin);
      link.classList.remove('is-underline-active');
    });

    link.addEventListener('pointercancel', () => {
      link.classList.remove('is-underline-active');
    });
  });
}

export function initNavigation() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const mobileToggle = header.querySelector('[data-mobile-menu-toggle]');
  const mobileMenu = header.querySelector('[data-mobile-menu]');
  const searchForm = header.querySelector('[data-search-form]');
  const searchInput = searchForm?.querySelector(
    '[data-predictive-search-input]',
  );
  const searchSubmit = searchForm?.querySelector('[data-search-submit]');
  const searchClear = searchForm?.querySelector(
    '[data-predictive-search-clear]',
  );
  const predictiveSearch = header.querySelector('[data-predictive-search]');
  const predictiveResults = predictiveSearch?.querySelector(
    '[data-predictive-search-results]',
  );
  const predictiveStatus = predictiveSearch?.querySelector(
    '[data-predictive-search-status]',
  );
  const menuIcon = mobileToggle?.querySelector('[data-menu-icon]');
  const closeIcon = mobileToggle?.querySelector('[data-close-icon]');
  const mobileMedia = window.matchMedia('(max-width: 1000px)');
  const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
  const megaMenuItems = [...header.querySelectorAll('[data-menu-item]')];
  const mobilePanels = [
    ...(mobileMenu?.querySelectorAll('[data-mobile-panel]') || []),
  ];
  let mobilePanelHistory = ['root'];
  let hoverCloseTimer = null;
  let lockedScrollY = null;
  let touchY = null;
  let restoringScroll = false;
  let predictiveSearchOpen = false;
  let predictiveSearchTimer = null;
  let predictiveSearchController = null;
  let renderedSearchQuery = '';

  const syncMobilePanels = () => {
    const activeId = mobilePanelHistory.at(-1) || 'root';
    const previousIds = new Set(mobilePanelHistory.slice(0, -1));

    mobilePanels.forEach((panel) => {
      const panelId = panel.dataset.mobilePanel;
      const active = panelId === activeId;
      panel.classList.toggle('is-active', active);
      panel.classList.toggle('is-previous', previousIds.has(panelId));
      panel.setAttribute('aria-hidden', String(!active));
      panel.inert = !active;
    });
  };

  const resetMobilePanels = () => {
    mobilePanelHistory = ['root'];
    mobileMenu?.classList.remove('is-going-back');
    syncMobilePanels();
  };

  const focusMobilePanel = (panelId, selector) => {
    const control = mobilePanels
      .find((panel) => panel.dataset.mobilePanel === panelId)
      ?.querySelector(selector);
    if (!control) return;

    requestAnimationFrame(() => {
      try {
        control.focus({ preventScroll: true });
      } catch {
        control.focus();
      }
    });
  };

  const navigationIsOpen = () =>
    mobileToggle?.getAttribute('aria-expanded') === 'true' ||
    megaMenuItems.some(
      (item) =>
        item
          .querySelector('[data-menu-trigger]')
          ?.getAttribute('aria-expanded') === 'true',
    );

  const syncScrollLock = () => {
    const menuOpen = navigationIsOpen();
    const shouldLock = menuOpen;

    if (shouldLock && lockedScrollY === null) lockedScrollY = window.scrollY;
    if (!shouldLock) lockedScrollY = null;
    document.body.classList.toggle('menu-open', menuOpen);
    document.body.classList.toggle('search-open', predictiveSearchOpen);
  };

  const scrollableNavigationParent = (target, deltaY) => {
    let element = target instanceof Element ? target : null;
    const boundary = element?.closest(
      '.mega-menu.is-visible, .mobile-navigation.is-open, .predictive-search.is-open',
    );
    if (!boundary) return null;

    while (element && boundary.contains(element)) {
      const { overflowY } = window.getComputedStyle(element);
      if (
        /(auto|scroll)/.test(overflowY) &&
        element.scrollHeight > element.clientHeight + 1
      ) {
        const canMoveDown =
          deltaY > 0 &&
          element.scrollTop + element.clientHeight < element.scrollHeight - 1;
        const canMoveUp = deltaY < 0 && element.scrollTop > 1;
        if (canMoveDown || canMoveUp) return element;
      }
      if (element === boundary) break;
      element = element.parentElement;
    }

    return null;
  };

  const preferredMegaMenuHeight = () => {
    const rootPixel =
      Number.parseFloat(
        window.getComputedStyle(document.documentElement).fontSize,
      ) || 1;

    if (window.innerWidth <= 1600) return 384 * rootPixel;
    if (window.innerWidth <= 2200) return 500 * rootPixel;
    return 574 * rootPixel;
  };

  const measureMegaMenuHeight = (targetItem = null) => {
    if (mobileMedia.matches) {
      header.style.removeProperty('--mega-menu-height');
      megaMenuItems.forEach((item) => {
        const menu = item.querySelector('[data-mega-menu]');
        menu?.style.removeProperty('--mega-menu-available-height');
        menu?.style.removeProperty('--mega-menu-content-height');
        menu?.style.removeProperty('--mega-menu-promo-width');
        menu?.style.removeProperty('--mega-menu-resolved-height');
      });
      return;
    }

    header.style.removeProperty('--mega-menu-height');
    const viewportHeight =
      document.documentElement.clientHeight || window.innerHeight;
    const preferredHeight = preferredMegaMenuHeight();
    const menuItem = targetItem?.matches?.('[data-menu-item]')
      ? targetItem
      : null;
    const items = menuItem ? [menuItem] : megaMenuItems;

    items.forEach((item) => {
      if (!item?.querySelector) return;
      const menu = item.querySelector('[data-mega-menu]');
      const outer = menu?.querySelector('.mega-menu__outer');
      const columns = menu?.querySelector('.mega-menu__columns');
      const promos = menu?.querySelector('.mega-menu__promos');
      if (!menu || !outer || !columns) return;

      const wasHidden = menu.hidden;
      if (wasHidden) menu.hidden = false;

      const menuTop = Math.max(0, menu.getBoundingClientRect().top);
      const outerStyles = window.getComputedStyle(outer);
      const verticalInsets =
        Number.parseFloat(outerStyles.paddingTop) +
        Number.parseFloat(outerStyles.paddingBottom) +
        Number.parseFloat(outerStyles.borderTopWidth) +
        Number.parseFloat(outerStyles.borderBottomWidth);
      const contentHeight = Math.ceil(columns.scrollHeight + verticalInsets);
      const availableHeight = Math.max(0, Math.floor(viewportHeight - menuTop));
      const resolvedHeight = Math.min(
        availableHeight,
        Math.max(preferredHeight, contentHeight),
      );

      menu.style.setProperty(
        '--mega-menu-available-height',
        `${availableHeight}px`,
      );
      menu.style.setProperty(
        '--mega-menu-content-height',
        `${contentHeight}px`,
      );
      menu.style.setProperty(
        '--mega-menu-resolved-height',
        `${resolvedHeight}px`,
      );

      const promoCount =
        promos?.querySelectorAll('.mega-menu__promo').length || 0;
      if (promos && promoCount) {
        const promoStyles = window.getComputedStyle(promos);
        const promoGap =
          Number.parseFloat(promoStyles.columnGap || promoStyles.gap) || 0;
        const availablePromoWidth = Math.max(
          0,
          promos.clientWidth - promoGap * (promoCount - 1),
        );
        const widthPerPromo = availablePromoWidth / promoCount;
        const availablePromoHeight = Math.max(
          0,
          resolvedHeight - verticalInsets,
        );
        const proportionalWidth = availablePromoHeight * (280 / 510);
        const promoWidth = Math.min(widthPerPromo, proportionalWidth);

        menu.style.setProperty(
          '--mega-menu-promo-width',
          `${Math.floor(promoWidth)}px`,
        );
      } else {
        menu.style.removeProperty('--mega-menu-promo-width');
      }

      if (wasHidden) menu.hidden = true;
    });
  };

  const closePredictiveSearch = ({ keepInputActive = false } = {}) => {
    if (predictiveSearchTimer) {
      window.clearTimeout(predictiveSearchTimer);
      predictiveSearchTimer = null;
    }
    predictiveSearchController?.abort();
    predictiveSearchController = null;
    predictiveSearchOpen = false;
    predictiveSearch?.classList.remove('is-open');
    if (predictiveSearch) predictiveSearch.hidden = true;
    predictiveSearch?.setAttribute('aria-busy', 'false');
    searchInput?.setAttribute('aria-expanded', 'false');
    searchForm?.classList.toggle('is-active', keepInputActive);
    syncScrollLock();
  };

  const openPredictiveSearch = () => {
    if (!predictiveSearch || !predictiveResults?.children.length) return;
    closeMegaMenus();
    setMobileMenu(false);
    predictiveSearchOpen = true;
    predictiveSearch.hidden = false;
    predictiveSearch.classList.add('is-open');
    searchInput?.setAttribute('aria-expanded', 'true');
    searchForm?.classList.add('is-active');
    syncScrollLock();
  };

  const searchResultsUrl = (query) => {
    const url = new URL(
      searchForm?.action || shopifyRoute('search'),
      window.location.origin,
    );
    const normalizedQuery = query.trim().replace(/\*+$/, '');
    const resultQuery = /\s/.test(normalizedQuery)
      ? normalizedQuery
      : `${normalizedQuery}*`;
    url.searchParams.set('q', resultQuery);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    return url.toString();
  };

  const requestPredictiveSearch = async (query) => {
    predictiveSearchController?.abort();
    const controller = new AbortController();
    predictiveSearchController = controller;
    predictiveSearch?.setAttribute('aria-busy', 'true');
    if (predictiveStatus) predictiveStatus.textContent = 'Loading suggestions.';

    const url = new URL(shopifyRoute('search/suggest'), window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,tag',
    );
    url.searchParams.set('resources[options][unavailable_products]', 'hide');
    url.searchParams.set('section_id', 'flb-predictive-search');

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: 'text/html' },
        signal: controller.signal,
      });
      if (!response.ok)
        throw new Error(`Predictive search failed (${response.status}).`);
      const responseMarkup = await response.text();
      if (searchInput?.value.trim() !== query) return;

      const template = document.createElement('template');
      template.innerHTML = responseMarkup;
      const sectionResults = template.content.querySelector(
        '#shopify-section-flb-predictive-search [data-predictive-search-section], [data-predictive-search-section]',
      );
      if (!sectionResults) {
        throw new Error('Predictive search section markup was not found.');
      }

      const collectionCount = Math.min(
        5,
        Number(sectionResults.dataset.collectionCount) || 0,
      );
      const productCount = Math.min(
        4,
        Number(sectionResults.dataset.productCount) || 0,
      );

      if (!collectionCount && !productCount) {
        renderedSearchQuery = query;
        if (predictiveResults) predictiveResults.innerHTML = '';
        if (predictiveStatus) {
          predictiveStatus.textContent = `No suggestions found for ${query}.`;
        }
        closePredictiveSearch({ keepInputActive: true });
        return;
      }

      if (predictiveResults) {
        const viewAll = sectionResults.querySelector(
          '[data-predictive-search-view-all]',
        );
        viewAll?.setAttribute('href', searchResultsUrl(query));
        predictiveResults.innerHTML = sectionResults.innerHTML;
        predictiveResults
          .querySelectorAll(
            '.predictive-search__product .product-card__details',
          )
          .forEach((option) => {
            option.dataset.predictiveSearchOption = '';
          });
      }
      initProductCards();
      renderedSearchQuery = query;
      if (predictiveStatus) {
        predictiveStatus.textContent = `${collectionCount} collection suggestions and ${productCount} product suggestions available.`;
      }
      openPredictiveSearch();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      if (predictiveStatus) {
        predictiveStatus.textContent =
          'Search suggestions are unavailable. Submit the search to view results.';
      }
      closePredictiveSearch({ keepInputActive: true });
    } finally {
      if (predictiveSearchController === controller) {
        predictiveSearchController = null;
        predictiveSearch?.setAttribute('aria-busy', 'false');
      }
    }
  };

  header
    .querySelectorAll('[data-mega-accordion-trigger]')
    .forEach((trigger) => {
      const panel = trigger
        .closest('.mega-menu__link-group')
        ?.querySelector('[data-mega-accordion-panel]');
      if (!panel) return;

      trigger.addEventListener('click', () => {
        const open = trigger.getAttribute('aria-expanded') !== 'true';
        trigger.setAttribute('aria-expanded', String(open));
        panel.hidden = !open;
        requestAnimationFrame(() => {
          measureMegaMenuHeight(trigger.closest('[data-menu-item]'));
        });
      });
    });

  const openMegaMenu = (item) => {
    closePredictiveSearch();
    if (hoverCloseTimer) {
      window.clearTimeout(hoverCloseTimer);
      hoverCloseTimer = null;
    }

    const currentItem = megaMenuItems.find(
      (candidate) =>
        candidate !== item &&
        candidate
          .querySelector('[data-menu-trigger]')
          ?.getAttribute('aria-expanded') === 'true',
    );
    const switching = Boolean(currentItem);

    measureMegaMenuHeight(item);
    setMegaMenu(item, true, { switching });
    if (currentItem) setMegaMenu(currentItem, false, { switching: true });
    syncScrollLock();
  };

  const scheduleMegaMenuClose = (item) => {
    if (hoverCloseTimer) window.clearTimeout(hoverCloseTimer);
    hoverCloseTimer = window.setTimeout(() => {
      setMegaMenu(item, false);
      syncScrollLock();
      hoverCloseTimer = null;
    }, 100);
  };

  function setMobileMenu(open) {
    const isOpen = mobileMedia.matches && open;
    if (isOpen) {
      closePredictiveSearch();
      resetMobilePanels();
    }
    mobileToggle?.setAttribute('aria-expanded', String(isOpen));
    mobileToggle?.setAttribute(
      'aria-label',
      isOpen ? 'Close menu' : 'Open menu',
    );
    mobileMenu?.classList.toggle('is-open', isOpen);
    if (mobileMenu) {
      mobileMenu.inert = mobileMedia.matches && !isOpen;
      if (mobileMedia.matches) {
        mobileMenu.setAttribute('aria-hidden', String(!isOpen));
      } else {
        mobileMenu.removeAttribute('aria-hidden');
      }
    }
    if (menuIcon) menuIcon.hidden = isOpen;
    if (closeIcon) closeIcon.hidden = !isOpen;
    syncScrollLock();
  }

  mobileMenu
    ?.querySelectorAll('[data-mobile-panel-open]')
    .forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const targetId = trigger.dataset.mobilePanelOpen;
        if (
          !mobilePanels.some((panel) => panel.dataset.mobilePanel === targetId)
        )
          return;
        mobileMenu?.classList.remove('is-going-back');
        mobilePanelHistory.push(targetId);
        syncMobilePanels();
        focusMobilePanel(targetId, 'button, a');
      });
    });

  mobileMenu
    ?.querySelectorAll('[data-mobile-panel-back]')
    .forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (mobilePanelHistory.length <= 1) return;
        mobileMenu?.classList.add('is-going-back');
        mobilePanelHistory.pop();
        syncMobilePanels();
        const activeId = mobilePanelHistory.at(-1);
        focusMobilePanel(activeId, '[data-mobile-panel-open], a');
      });
    });

  mobileMenu
    ?.querySelectorAll('[data-mobile-menu-close]')
    .forEach((trigger) => {
      trigger.addEventListener('click', () => {
        setMobileMenu(false);
        mobileToggle?.focus();
      });
    });

  mobileToggle?.addEventListener('click', () => {
    const open = mobileToggle.getAttribute('aria-expanded') !== 'true';
    setMobileMenu(open);
    if (open) {
      mobileMenu?.querySelector('a, button:not([disabled])')?.focus();
    }
  });

  header.querySelectorAll('[data-menu-item]').forEach((item) => {
    const trigger = item.querySelector('[data-menu-trigger]');
    const menu = item.querySelector('[data-mega-menu]');
    if (!trigger || !menu) return;

    trigger.addEventListener('click', () => {
      const open = trigger.getAttribute('aria-expanded') !== 'true';
      if (open) openMegaMenu(item);
      else {
        setMegaMenu(item, false);
        syncScrollLock();
      }
    });

    item.addEventListener('pointerenter', () => {
      if (!hoverMedia.matches || mobileMedia.matches) return;
      openMegaMenu(item);
    });

    item.addEventListener('pointerleave', (event) => {
      if (!hoverMedia.matches || mobileMedia.matches) return;
      const nextItem =
        event.relatedTarget instanceof Element
          ? event.relatedTarget.closest('[data-menu-item]')
          : null;
      if (nextItem?.querySelector('[data-mega-menu]')) return;
      if (!item.contains(document.activeElement)) scheduleMegaMenuClose(item);
    });

    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget) && !item.matches(':hover')) {
        setMegaMenu(item, false);
        syncScrollLock();
      }
    });
  });

  const currentSearch = new URLSearchParams(window.location.search)
    .get('q')
    ?.trim()
    .replace(/\*+$/, '');

  if (searchInput && currentSearch) {
    searchInput.value = currentSearch;
    if (searchClear) searchClear.hidden = false;
  }

  searchSubmit?.addEventListener('click', (event) => {
    if (searchForm?.classList.contains('is-active')) return;

    event.preventDefault();
    searchForm?.classList.add('is-active');
    closeMegaMenus();
    setMobileMenu(false);
    syncScrollLock();

    try {
      searchInput?.focus({ preventScroll: true });
    } catch {
      searchInput?.focus();
    }
  });

  const queuePredictiveSearch = (query, delay = 180) => {
    if (predictiveSearchTimer) window.clearTimeout(predictiveSearchTimer);
    predictiveSearchTimer = window.setTimeout(() => {
      predictiveSearchTimer = null;
      requestPredictiveSearch(query);
    }, delay);
  };

  searchInput?.addEventListener('pointerdown', () => {
    if (document.activeElement === searchInput) return;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    requestAnimationFrame(() => {
      if (document.activeElement === searchInput) {
        window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
      }
    });
  });

  searchInput?.addEventListener('focus', () => {
    searchForm?.classList.add('is-active');
    closeMegaMenus();
    setMobileMenu(false);
    syncScrollLock();

    const query = searchInput.value.trim().replace(/\*+$/, '');
    if (!query) return;
    if (query === renderedSearchQuery && predictiveResults?.children.length) {
      openPredictiveSearch();
    } else {
      queuePredictiveSearch(query, 0);
    }
  });

  searchInput?.addEventListener('input', () => {
    const query = searchInput.value.trim().replace(/\*+$/, '');
    searchForm?.classList.add('is-active');
    if (searchClear) searchClear.hidden = !query;

    if (!query) {
      renderedSearchQuery = '';
      if (predictiveResults) predictiveResults.innerHTML = '';
      if (predictiveStatus) predictiveStatus.textContent = '';
      closePredictiveSearch({ keepInputActive: true });
      return;
    }

    queuePredictiveSearch(query);
  });

  searchInput?.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowDown' || !predictiveSearchOpen) return;
    const firstOption = predictiveSearch?.querySelector(
      '[data-predictive-search-option]',
    );
    if (!firstOption) return;
    event.preventDefault();
    firstOption.focus();
  });

  searchClear?.addEventListener('click', () => {
    searchInput.value = '';
    searchClear.hidden = true;
    renderedSearchQuery = '';
    if (predictiveResults) predictiveResults.innerHTML = '';
    if (predictiveStatus) predictiveStatus.textContent = '';
    closePredictiveSearch({ keepInputActive: true });
    searchInput.focus({ preventScroll: true });
  });

  predictiveSearch?.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const options = [
      ...predictiveSearch.querySelectorAll('[data-predictive-search-option]'),
    ];
    const currentIndex = options.indexOf(document.activeElement);
    if (currentIndex < 0) return;
    event.preventDefault();

    if (event.key === 'ArrowDown') {
      options[(currentIndex + 1) % options.length]?.focus();
    } else if (currentIndex === 0) {
      searchInput?.focus({ preventScroll: true });
    } else {
      options[currentIndex - 1]?.focus();
    }
  });

  predictiveSearch?.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (
      event.target.closest(
        '[data-predictive-search-option], .predictive-search__product .product-card__media, .predictive-search__product [data-quick-add]',
      )
    ) {
      closePredictiveSearch();
    }
  });

  searchForm?.addEventListener('submit', (event) => {
    if (searchInput?.value.trim()) {
      closePredictiveSearch();
      return;
    }
    event.preventDefault();
    searchInput?.focus({ preventScroll: true });
  });

  mobileMenu?.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => {
      closeMegaMenus();
      setMobileMenu(false);
      syncScrollLock();
    });
  });

  document.addEventListener('click', (event) => {
    if (!header.contains(event.target)) {
      closeMegaMenus();
      closePredictiveSearch();
      syncScrollLock();
      return;
    }

    if (
      predictiveSearchOpen &&
      !searchForm?.contains(event.target) &&
      !predictiveSearch?.contains(event.target)
    ) {
      closePredictiveSearch();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (
      event.key === 'Tab' &&
      mobileToggle?.getAttribute('aria-expanded') === 'true'
    ) {
      const focusable = [
        mobileToggle,
        ...mobileMenu.querySelectorAll(
          'a, button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => !element.closest('[aria-hidden="true"]'));
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    if (event.key !== 'Escape') return;
    const expandedMenuTrigger = header.querySelector(
      '[data-menu-trigger][aria-expanded="true"]',
    );
    const mobileWasOpen =
      mobileToggle?.getAttribute('aria-expanded') === 'true';
    const predictiveSearchWasOpen = predictiveSearchOpen;
    closeMegaMenus();
    setMobileMenu(false);
    closePredictiveSearch({ keepInputActive: predictiveSearchWasOpen });
    syncScrollLock();
    if (expandedMenuTrigger) expandedMenuTrigger.focus();
    else if (mobileWasOpen) mobileToggle.focus();
    else if (predictiveSearchWasOpen)
      searchInput?.focus({ preventScroll: true });
  });

  mobileMedia.addEventListener?.('change', () => {
    closeMegaMenus();
    setMobileMenu(false);
    closePredictiveSearch();
    measureMegaMenuHeight();
    syncScrollLock();
  });

  document.addEventListener(
    'wheel',
    (event) => {
      if (lockedScrollY === null) return;
      if (scrollableNavigationParent(event.target, event.deltaY)) return;
      event.preventDefault();
    },
    { capture: true, passive: false },
  );

  document.addEventListener(
    'touchstart',
    (event) => {
      touchY = event.touches[0]?.clientY ?? null;
    },
    { passive: true },
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      if (lockedScrollY === null || touchY === null) return;
      const currentY = event.touches[0]?.clientY ?? touchY;
      const deltaY = touchY - currentY;
      touchY = currentY;
      if (scrollableNavigationParent(event.target, deltaY)) return;
      event.preventDefault();
    },
    { passive: false },
  );

  document.addEventListener('keydown', (event) => {
    if (lockedScrollY === null) return;
    if (
      event.target instanceof Element &&
      event.target.matches('input, textarea, select, button, [contenteditable]')
    ) {
      return;
    }

    const keyDelta = {
      ArrowDown: 40,
      ArrowUp: -40,
      PageDown: window.innerHeight * 0.8,
      PageUp: window.innerHeight * -0.8,
      ' ': event.shiftKey
        ? window.innerHeight * -0.8
        : window.innerHeight * 0.8,
    }[event.key];
    const isBoundaryKey = event.key === 'Home' || event.key === 'End';
    if (keyDelta === undefined && !isBoundaryKey) return;

    const scrollParent = scrollableNavigationParent(
      event.target,
      keyDelta || (event.key === 'End' ? 1 : -1),
    );
    event.preventDefault();
    if (!scrollParent) return;

    if (event.key === 'Home') scrollParent.scrollTop = 0;
    else if (event.key === 'End')
      scrollParent.scrollTop = scrollParent.scrollHeight;
    else scrollParent.scrollBy({ top: keyDelta, behavior: 'auto' });
  });

  window.addEventListener(
    'scroll',
    () => {
      if (
        lockedScrollY === null ||
        restoringScroll ||
        window.scrollY === lockedScrollY
      ) {
        return;
      }
      restoringScroll = true;
      window.scrollTo(window.scrollX, lockedScrollY);
      window.requestAnimationFrame(() => {
        restoringScroll = false;
      });
    },
    { passive: true },
  );

  window.addEventListener('resize', () => measureMegaMenuHeight());
  measureMegaMenuHeight();
  setMobileMenu(false);
}

export function initSearchForms() {
  const currentQuery = new URLSearchParams(window.location.search)
    .get('q')
    ?.trim()
    .replace(/\*+$/, '');

  document.querySelectorAll('[data-search-form]').forEach((form) => {
    if (form.dataset.searchInitialized === 'true') return;
    form.dataset.searchInitialized = 'true';

    const input = form.querySelector('input[type="search"][name="q"]');
    if (!input) return;

    input.value = currentQuery || input.value.replace(/\*+$/, '');

    form.addEventListener('submit', (event) => {
      const query = input.value.trim().replace(/\*+$/, '');
      if (!query) {
        event.preventDefault();
        input.focus({ preventScroll: true });
        return;
      }

      const isSingleTerm = !/\s/.test(query);
      input.value = isSingleTerm ? `${query}*` : query;
    });
  });
}

function getSwiperConstructor() {
  const SwiperConstructor = window.Swiper;
  if (typeof SwiperConstructor !== 'function') {
    console.error('Swiper failed to load.');
    return null;
  }
  return SwiperConstructor;
}

export function initAnnouncementSliders() {
  const SwiperConstructor = getSwiperConstructor();
  if (!SwiperConstructor) return;

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  document.querySelectorAll('[data-announcement-slider]').forEach((slider) => {
    const viewport = slider.querySelector('[data-announcement-swiper]');
    const slides = [...slider.querySelectorAll('[data-announcement-slide]')];
    const previous = slider.querySelector('[data-announcement-previous]');
    const next = slider.querySelector('[data-announcement-next]');
    const autoplaySeconds = Number(slider.dataset.autoplaySeconds) || 0;
    if (!viewport || !slides.length) return;

    const sync = (swiper) => {
      slides.forEach((slide, index) => {
        const active = index === swiper.activeIndex;
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
    };

    if (slides.length === 1) {
      slides[0].setAttribute('aria-hidden', 'false');
      return;
    }

    const swiper = new SwiperConstructor(viewport, {
      slidesPerView: 1,
      spaceBetween: 0,
      rewind: true,
      speed: reducedMotion ? 0 : 350,
      allowTouchMove: true,
      navigation: {
        prevEl: previous,
        nextEl: next,
      },
      autoplay:
        !reducedMotion && autoplaySeconds > 0
          ? {
              delay: autoplaySeconds * 1000,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
            }
          : false,
      a11y: {
        containerMessage: slider.getAttribute('aria-label'),
      },
      on: {
        init(instance) {
          sync(instance);
        },
        slideChange(instance) {
          sync(instance);
        },
      },
    });

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        swiper.slidePrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        swiper.slideNext();
      }
    });

    slider.addEventListener('focusin', () => swiper.autoplay?.stop());
    slider.addEventListener('focusout', (event) => {
      if (!slider.contains(event.relatedTarget)) swiper.autoplay?.start();
    });
  });
}
function initHeroFeatureVideo(slider, swiper, status, sync) {
  const track = slider.querySelector('[data-hero-track]');
  const triggers = [...slider.querySelectorAll('[data-hero-video-trigger]')];
  const players = [...slider.querySelectorAll('[data-hero-video-player]')];
  if (!triggers.length || !players.length) return;

  let activePlayer = null;
  let activeTrigger = null;
  let pausedBackgroundVideos = [];
  let previousStatusText = '';

  const closeVideo = ({ exitFullscreen = true, restoreFocus = true } = {}) => {
    if (!activePlayer) return;

    const player = activePlayer;
    const trigger = activeTrigger;
    const video = player.querySelector('video');
    activePlayer = null;
    activeTrigger = null;

    if (exitFullscreen) {
      const fullscreenElement = document.fullscreenElement;
      if (
        fullscreenElement &&
        (fullscreenElement === video || player.contains(fullscreenElement))
      ) {
        document.exitFullscreen?.().catch(() => {});
      } else if (
        video?.webkitDisplayingFullscreen &&
        typeof video.webkitExitFullscreen === 'function'
      ) {
        try {
          video.webkitExitFullscreen();
        } catch {
          // The native player may already be closing.
        }
      }
    }

    video?.pause();
    if (video) {
      video.controls = false;
      try {
        video.currentTime = 0;
      } catch {
        // The video metadata may not be available yet.
      }
    }

    player.hidden = true;
    player.setAttribute('aria-hidden', 'true');
    delete player.dataset.mobileFullscreen;
    trigger?.setAttribute('aria-expanded', 'false');
    slider.classList.remove('is-feature-video-active');
    if (track) {
      track.inert = false;
      track.removeAttribute('aria-hidden');
    }

    pausedBackgroundVideos.forEach((backgroundVideo) => {
      backgroundVideo.play().catch(() => {});
    });
    pausedBackgroundVideos = [];
    swiper.autoplay?.start();
    sync(swiper, false);
    if (status) status.textContent = previousStatusText;

    if (restoreFocus && trigger) {
      window.requestAnimationFrame(() =>
        trigger.focus({ preventScroll: true }),
      );
    }
  };

  const enterMobileFullscreen = (player, video) => {
    player.dataset.mobileFullscreen = 'true';

    try {
      if (typeof video.requestFullscreen === 'function') {
        video.requestFullscreen().catch(() => {
          delete player.dataset.mobileFullscreen;
        });
      } else if (typeof video.webkitEnterFullscreen === 'function') {
        video.webkitEnterFullscreen();
      } else if (typeof player.requestFullscreen === 'function') {
        player.requestFullscreen().catch(() => {
          delete player.dataset.mobileFullscreen;
        });
      } else {
        delete player.dataset.mobileFullscreen;
      }
    } catch {
      // Keep the fixed, full-viewport player as a mobile fallback.
      delete player.dataset.mobileFullscreen;
    }
  };

  const openVideo = (trigger) => {
    const playerId = trigger.getAttribute('aria-controls');
    const player = playerId ? document.getElementById(playerId) : null;
    const video = player?.querySelector('video');
    if (!player || !video || !slider.contains(player)) return;

    const mobile = window.matchMedia('(max-width: 1000px)').matches;
    activePlayer = player;
    activeTrigger = trigger;
    previousStatusText = status?.textContent || '';
    pausedBackgroundVideos = [
      ...slider.querySelectorAll('.hhero__video'),
    ].filter((backgroundVideo) => !backgroundVideo.paused);
    pausedBackgroundVideos.forEach((backgroundVideo) =>
      backgroundVideo.pause(),
    );

    swiper.autoplay?.stop();
    slider.classList.add('is-feature-video-active');
    if (track) {
      track.inert = true;
      track.setAttribute('aria-hidden', 'true');
    }
    player.hidden = false;
    player.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    video.controls = mobile;
    try {
      video.currentTime = 0;
    } catch {
      // The video metadata may not be available yet.
    }
    video.play().catch(() => {
      if (status)
        status.textContent = 'The video could not start automatically.';
    });

    if (mobile) {
      enterMobileFullscreen(player, video);
    } else {
      player.querySelector('[data-hero-video-close]')?.focus();
    }
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', () => openVideo(trigger));
  });

  players.forEach((player) => {
    player
      .querySelector('[data-hero-video-close]')
      ?.addEventListener('click', () => closeVideo());
    const video = player.querySelector('video');
    video?.addEventListener('ended', () => closeVideo());
    video?.addEventListener('webkitendfullscreen', () =>
      closeVideo({ exitFullscreen: false, restoreFocus: false }),
    );
  });

  document.addEventListener('fullscreenchange', () => {
    if (
      activePlayer?.dataset.mobileFullscreen === 'true' &&
      !document.fullscreenElement
    ) {
      closeVideo({ exitFullscreen: false, restoreFocus: false });
    }
  });

  slider.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !activePlayer) return;
    event.preventDefault();
    closeVideo();
  });
}

export function initHeroSliders() {
  const SwiperConstructor = getSwiperConstructor();
  if (!SwiperConstructor) return;

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  document.querySelectorAll('[data-hero-slider]').forEach((slider) => {
    if (slider.dataset.heroSliderInitialized === 'true') return;

    const slides = [...slider.querySelectorAll('[data-hero-slide]')];

    const dots = [...slider.querySelectorAll('[data-hero-dot]')];
    const previous = slider.querySelector('[data-hero-previous]');
    const next = slider.querySelector('[data-hero-next]');
    const status = slider.querySelector('[data-hero-status]');
    const autoplaySeconds = Number(slider.dataset.autoplaySeconds) || 0;
    if (!slides.length) return;
    slider.dataset.heroSliderInitialized = 'true';

    const sync = (swiper, announce = true) => {
      const activeIndex = swiper.realIndex;

      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });

      dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === activeIndex));
      });

      if (announce && status) {
        const title = slides[activeIndex]
          ?.querySelector('.hhero__title')
          ?.textContent?.trim();
        status.textContent = `Slide ${activeIndex + 1} of ${slides.length}${title ? `: ${title}` : ''}`;
      }
    };

    const swiper = new SwiperConstructor(slider, {
      effect: 'fade',
      fadeEffect: { crossFade: true },
      rewind: slides.length > 1,
      speed: reducedMotion ? 0 : 1050,
      allowTouchMove: slides.length > 1,
      autoplay:
        !reducedMotion && autoplaySeconds > 0 && slides.length > 1
          ? {
              delay: autoplaySeconds * 1000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
      a11y: {
        containerMessage: slider.getAttribute('aria-label'),
      },
      on: {
        init(instance) {
          sync(instance, false);
        },
        slideChange(instance) {
          sync(instance);
        },
      },
    });

    previous?.addEventListener('click', () => swiper.slidePrev());
    next?.addEventListener('click', () => swiper.slideNext());
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => swiper.slideTo(index));
    });

    initHeroFeatureVideo(slider, swiper, status, sync);

    slider.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        swiper.slidePrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        swiper.slideNext();
      } else {
        return;
      }
      swiper.autoplay?.start();
    });

    slider.addEventListener('focusin', () => swiper.autoplay?.stop());
    slider.addEventListener('focusout', (event) => {
      if (!slider.contains(event.relatedTarget)) swiper.autoplay?.start();
    });
  });
}

export function initBenefitsSliders() {
  const SwiperConstructor = getSwiperConstructor();
  if (!SwiperConstructor) return;

  document.querySelectorAll('[data-benefits-slider]').forEach((viewport) => {
    const section = viewport.closest('.benefits');
    const dots = [...(section?.querySelectorAll('[data-benefit-dot]') || [])];
    const slides = [...viewport.querySelectorAll('.benefits__item')];
    if (!section || !slides.length) return;

    const sync = (swiper) => {
      dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === swiper.realIndex));
      });
    };

    const swiper = new SwiperConstructor(viewport, {
      slidesPerView: 1,
      spaceBetween: 0,
      rewind: slides.length > 1,
      allowTouchMove: slides.length > 1,
      autoplay: {
        delay: 4500,
        disableOnInteraction: false,
      },
      breakpoints: {
        1001: {
          slidesPerView: 4,
          allowTouchMove: false,
          autoplay: false,
        },
      },
      on: {
        init(instance) {
          sync(instance);
        },
        slideChange(instance) {
          sync(instance);
        },
      },
    });

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => swiper.slideTo(index));
    });
  });
}

const sliderProgressTimers = new WeakMap();
const sliderProgressVisibleDuration = 1800;

function setSliderProgressActive(slider, active, delayed = false) {
  const activeTimer = sliderProgressTimers.get(slider);
  if (activeTimer) window.clearTimeout(activeTimer);
  sliderProgressTimers.delete(slider);

  if (!slider.querySelector('[data-slider-progress]')) return;
  slider.classList.toggle('is-progress-active', active);

  if (active && delayed) {
    sliderProgressTimers.set(
      slider,
      window.setTimeout(() => {
        slider.classList.remove('is-progress-active');
        sliderProgressTimers.delete(slider);
      }, sliderProgressVisibleDuration),
    );
  }
}

function setSliderDragging(slider, dragging) {
  const wasDragging = slider.classList.contains('is-dragging');
  slider.classList.toggle('is-dragging', dragging);

  if (dragging) setSliderProgressActive(slider, true);
  else if (wasDragging) setSliderProgressActive(slider, true, true);
}

const recentlyViewedStorageKey = 'viewedProducts';
const recentlyViewedUrlStorageKey = 'flbViewedProductUrls';
const recentlyViewedMaxProducts = 24;

function getRecentlyViewedProductIds() {
  try {
    const storedProducts = JSON.parse(
      window.localStorage.getItem(recentlyViewedStorageKey) || '[]',
    );
    if (!Array.isArray(storedProducts)) return [];

    return [
      ...new Set(
        storedProducts
          .map((entry) =>
            typeof entry === 'object' && entry !== null ? entry.id : entry,
          )
          .filter(Boolean)
          .map(String),
      ),
    ];
  } catch {
    return [];
  }
}

function getRecentlyViewedProductUrls() {
  try {
    const storedUrls = JSON.parse(
      window.localStorage.getItem(recentlyViewedUrlStorageKey) || '{}',
    );
    return storedUrls &&
      typeof storedUrls === 'object' &&
      !Array.isArray(storedUrls)
      ? storedUrls
      : {};
  } catch {
    return {};
  }
}

function recordRecentlyViewedProduct() {
  const productDetail = document.querySelector(
    '[data-product-detail][data-product-id][data-product-detail-url]',
  );
  if (!productDetail) return;

  const productId = String(productDetail.dataset.productId || '');
  const rawProductUrl = productDetail.dataset.productDetailUrl;
  if (!productId || !rawProductUrl) return;

  try {
    const productUrl = new URL(rawProductUrl, window.location.origin);
    productUrl.search = '';
    productUrl.hash = '';

    const productIds = [
      productId,
      ...getRecentlyViewedProductIds().filter((id) => id !== productId),
    ].slice(0, recentlyViewedMaxProducts);
    const storedUrls = getRecentlyViewedProductUrls();
    const productUrls = productIds.reduce((urls, id) => {
      const url = id === productId ? productUrl.pathname : storedUrls[id];
      if (url) urls[id] = url;
      return urls;
    }, {});

    window.localStorage.setItem(
      recentlyViewedStorageKey,
      JSON.stringify(productIds),
    );
    window.localStorage.setItem(
      recentlyViewedUrlStorageKey,
      JSON.stringify(productUrls),
    );
  } catch {
    // Browsing history is optional when storage is unavailable.
  }
}

function parseProductFeed(html) {
  const documentFragment = new DOMParser().parseFromString(html, 'text/html');
  return [
    ...documentFragment.querySelectorAll(
      '[data-flb-product-card-feed] > [data-feed-product-id]',
    ),
  ];
}

async function fetchProductFeed(url) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: { Accept: 'text/html' },
  });
  if (!response.ok)
    throw new Error(`Product feed request failed: ${response.status}`);
  return parseProductFeed(await response.text());
}

async function getRecentlyViewedSlides(slider, limit) {
  const currentProductId = slider.dataset.currentProductId;
  const excludeCurrent = slider.dataset.excludeCurrentProduct === 'true';
  const productUrls = getRecentlyViewedProductUrls();
  const productIds = getRecentlyViewedProductIds()
    .filter((id) => !excludeCurrent || id !== currentProductId)
    .filter((id) => productUrls[id])
    .slice(0, limit);

  const slideGroups = await Promise.all(
    productIds.map(async (productId) => {
      try {
        const productUrl = new URL(
          productUrls[productId],
          window.location.origin,
        );
        productUrl.searchParams.set('view', 'flb-product-feed');
        const slides = await fetchProductFeed(productUrl);
        return slides.find(
          (slide) => slide.dataset.feedProductId === productId,
        );
      } catch {
        return null;
      }
    }),
  );

  return slideGroups.filter(Boolean);
}

async function getRelatedProductSlides(slider, limit) {
  const recommendationsUrl = slider.dataset.recommendationsUrl;
  if (!recommendationsUrl) return [];

  const response = await fetch(recommendationsUrl, {
    credentials: 'same-origin',
    headers: { Accept: 'text/html' },
  });
  if (!response.ok) {
    throw new Error(
      `Product recommendations request failed: ${response.status}`,
    );
  }

  const responseDocument = new DOMParser().parseFromString(
    await response.text(),
    'text/html',
  );
  const renderedSlider = responseDocument.getElementById(slider.id);
  return [
    ...(renderedSlider?.querySelectorAll(
      '[data-slider-track] > .swiper-slide',
    ) || []),
  ].slice(0, limit);
}

async function getCollectionBestSellerSlides(slider, limit) {
  const productFeedUrl = slider.dataset.productFeedUrl;
  if (!productFeedUrl) return [];

  const slides = await fetchProductFeed(productFeedUrl);
  return rankProductFeedSlides(slider, slides, limit);
}

function rankProductFeedSlides(slider, slides, limit) {
  const currentProductId = slider.dataset.currentProductId;
  const currentProductType = (slider.dataset.currentProductType || '')
    .trim()
    .toLocaleLowerCase();
  const excludeCurrent = slider.dataset.excludeCurrentProduct === 'true';
  const eligibleSlides = slides.filter(
    (slide) =>
      !excludeCurrent || slide.dataset.feedProductId !== currentProductId,
  );
  const differentTypeSlides = [];
  const sameTypeSlides = [];

  eligibleSlides.forEach((slide) => {
    const productType = (slide.dataset.feedProductType || '')
      .trim()
      .toLocaleLowerCase();
    if (currentProductType && productType !== currentProductType) {
      differentTypeSlides.push(slide);
    } else {
      sameTypeSlides.push(slide);
    }
  });

  return [...differentTypeSlides, ...sameTypeSlides].slice(0, limit);
}

function getInlineProductSliderFallback(slider, limit) {
  const template = slider.querySelector(
    'template[data-product-slider-fallback]',
  );
  if (!(template instanceof HTMLTemplateElement)) return [];

  const slides = [
    ...template.content.querySelectorAll('[data-feed-product-id]'),
  ].map((slide) => slide.cloneNode(true));

  return rankProductFeedSlides(slider, slides, limit);
}

async function getCollectionFallbackSlides(slider, limit) {
  try {
    const feedSlides = await getCollectionBestSellerSlides(slider, limit);
    if (feedSlides.length) return feedSlides;
  } catch (error) {
    console.warn(
      'Could not load the product slider feed; using inline products.',
      error,
    );
  }

  return getInlineProductSliderFallback(slider, limit);
}

function setRecentlyViewedFallbackHeading(slider) {
  const fallbackTitle =
    slider.dataset.recentlyViewedFallbackTitle || 'You may also like';
  const heading = slider.querySelector('.section-heading__title');
  const track = slider.querySelector('[data-slider-track]');

  slider.classList.add('product-slider--recently-viewed-fallback');
  if (heading) heading.textContent = fallbackTitle;
  if (track) track.setAttribute('aria-label', `${fallbackTitle} products`);
}

function finishDeferredProductSlider(slider, slides) {
  const track = slider.querySelector('[data-slider-track]');
  if (!track || !slides.length) {
    slider.removeAttribute('aria-busy');
    slider.dataset.sliderSourceLoaded = 'true';
    slider.hidden = !window.Shopify?.designMode;
    return;
  }

  track.replaceChildren(...slides);
  slider.dataset.sliderSourceLoaded = 'true';
  slider.removeAttribute('aria-busy');
  slider.hidden = false;
  initProductCards();
  initSwiperSliders();
}

async function loadDeferredProductSlider(slider) {
  if (
    slider.dataset.sliderSourceLoading === 'true' ||
    slider.dataset.sliderSourceLoaded === 'true'
  ) {
    return;
  }

  slider.dataset.sliderSourceLoading = 'true';
  const limit = Math.min(
    recentlyViewedMaxProducts,
    Math.max(1, Number(slider.dataset.productLimit) || 12),
  );

  try {
    let slides = [];
    if (slider.dataset.productSliderSource === 'recently_viewed') {
      slides = await getRecentlyViewedSlides(slider, limit);
      if (!slides.length) {
        slides = await getCollectionFallbackSlides(slider, limit);
        if (slides.length) setRecentlyViewedFallbackHeading(slider);
      }
    } else if (slider.dataset.productSliderSource === 'related_products') {
      slides = await getRelatedProductSlides(slider, limit);
    } else if (
      slider.dataset.productSliderSource === 'collection_best_sellers'
    ) {
      slides = await getCollectionFallbackSlides(slider, limit);
    }
    finishDeferredProductSlider(slider, slides);
  } catch (error) {
    console.error('Could not load product slider products.', error);
    finishDeferredProductSlider(slider, []);
  } finally {
    delete slider.dataset.sliderSourceLoading;
  }
}

export function initProductSliderSources() {
  recordRecentlyViewedProduct();
  document
    .querySelectorAll('[data-product-slider-source][data-slider-deferred]')
    .forEach((slider) => loadDeferredProductSlider(slider));
}

export function initProductReviewAccordions() {
  document
    .querySelectorAll('[data-product-reviews-accordion]')
    .forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;
      if (details.dataset.accordionInitialized === 'true') return;

      const summary = details.querySelector(':scope > summary');
      if (!(summary instanceof HTMLElement)) return;

      details.dataset.accordionInitialized = 'true';
      summary.addEventListener('click', (event) => {
        if (
          event.target instanceof Element &&
          event.target.closest('a, button')
        )
          return;
        event.preventDefault();
        details.open = !details.open;
      });
    });
}

export function initSwiperSliders() {
  const SwiperConstructor = getSwiperConstructor();
  if (!SwiperConstructor) return;

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  document.querySelectorAll('[data-slider]').forEach((slider) => {
    if (
      slider.dataset.sliderDeferred === 'true' &&
      slider.dataset.sliderSourceLoaded !== 'true'
    ) {
      return;
    }
    if (
      slider.dataset.accountOrdersEmpty === 'true' ||
      slider.dataset.accountOffersEmpty === 'true'
    ) {
      return;
    }
    const viewport = slider.querySelector('[data-slider-swiper]');
    const scrollbar = slider.querySelector('.slider-progress');
    const previous = slider.querySelector('[data-slider-previous]');
    const next = slider.querySelector('[data-slider-next]');
    const mobileSlides = Number(slider.dataset.slidesMobile) || 1.8;
    const mobileSpace = Number(slider.dataset.spaceMobile) || 16;
    const desktopSlides = Number(slider.dataset.slidesDesktop) || 4;
    const desktopSpace = Number(slider.dataset.spaceDesktop) || 16;
    if (!viewport || viewport.swiper) return;

    const swiper = new SwiperConstructor(viewport, {
      speed: reducedMotion ? 0 : 700,
      grabCursor: true,
      watchOverflow: true,
      followFinger: true,
      threshold: 3,
      resistance: true,
      resistanceRatio: 0.82,
      touchRatio: 0.95,
      slidesPerView: mobileSlides,
      slidesPerGroup: 1,
      spaceBetween: mobileSpace,
      allowTouchMove: true,
      loop: false,
      freeMode: {
        enabled:
          !reducedMotion && !slider.classList.contains('flb-account-slider'),
        momentum: true,
        momentumRatio: 0.7,
        momentumVelocityRatio: 0.8,
        momentumBounce: true,
        momentumBounceRatio: 0.35,
        minimumVelocity: 0.02,
        sticky: true,
      },
      navigation: {
        prevEl: previous,
        nextEl: next,
      },
      scrollbar: scrollbar
        ? {
            el: scrollbar,
            dragClass: 'slider-progress__bar',
            draggable: true,
            snapOnRelease: true,
            lockClass: 'is-disabled',
            horizontalClass: 'slider-progress--horizontal',
            scrollbarDisabledClass: 'slider-progress--disabled',
          }
        : false,
      a11y: {
        containerMessage: slider.getAttribute('aria-labelledby')
          ? 'Product carousel'
          : slider.getAttribute('aria-label'),
      },
      breakpoints: {
        1001: {
          slidesPerView: desktopSlides,
          slidesPerGroup: desktopSlides,
          spaceBetween: desktopSpace,
          centeredSlides: false,
          loop: false,
          freeMode: {
            enabled: false,
          },
        },
      },
      on: {
        slideChange(instance) {
          if (instance.initialized) {
            setSliderProgressActive(slider, true, true);
          }
        },
        sliderFirstMove() {
          setSliderDragging(slider, true);
        },
        touchEnd() {
          setSliderDragging(slider, false);
        },
        scrollbarDragStart() {
          setSliderDragging(slider, true);
        },
        scrollbarDragEnd() {
          setSliderDragging(slider, false);
        },
      },
    });

    viewport.addEventListener('keydown', (event) => {
      if (event.target !== viewport) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        swiper.slidePrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        swiper.slideNext();
      } else if (event.key === 'Home') {
        event.preventDefault();
        swiper.slideTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        swiper.slideTo(swiper.slides.length - 1);
      } else {
        return;
      }
      setSliderProgressActive(slider, true, true);
    });
  });
}

export function initStorySliders() {
  const SwiperConstructor = getSwiperConstructor();
  if (!SwiperConstructor) return;

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  document.querySelectorAll('[data-story-slider]').forEach((slider) => {
    const viewport = slider.querySelector('[data-story-swiper]');
    const slides = [...slider.querySelectorAll('[data-story-slide]')];
    const copies = [...slider.querySelectorAll('[data-story-copy]')];
    const scrollbar = slider.querySelector('.slider-progress');
    const previous = slider.querySelector('[data-slider-previous]');
    const next = slider.querySelector('[data-slider-next]');
    const status = slider.querySelector('[data-story-status]');
    const dialog = slider.querySelector('[data-story-dialog]');
    if (!viewport || !slides.length) return;

    const closeDialog = () => {
      if (!dialog?.open) return;
      dialog.close();
    };

    slider.querySelectorAll('[data-story-open]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (!dialog) return;
        const index = Number(trigger.dataset.storyIndex) || 0;
        const storySlide = slides[index];
        const storyCopy = copies[index];
        const sourceImage = storySlide?.querySelector('img');
        const dialogImage = dialog.querySelector('[data-story-dialog-image]');
        const dialogTitle = dialog.querySelector('[data-story-dialog-title]');
        const dialogText = dialog.querySelector('[data-story-dialog-text]');
        const popupImageSource = trigger.dataset.storyPopupImage;

        if (dialogImage) {
          const imageSource =
            popupImageSource || sourceImage?.currentSrc || sourceImage?.src;
          if (imageSource) dialogImage.src = imageSource;
          dialogImage.alt = sourceImage?.alt || '';
        }
        if (dialogTitle) {
          dialogTitle.textContent =
            storyCopy?.querySelector('h3')?.textContent?.trim() || '';
        }
        if (dialogText) {
          dialogText.textContent =
            storyCopy?.querySelector('p')?.textContent?.trim() || '';
        }

        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.body.classList.add('story-open');
        dialog.querySelector('[data-story-close]')?.focus();
      });
    });

    dialog
      ?.querySelector('[data-story-close]')
      ?.addEventListener('click', closeDialog);
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog?.addEventListener('close', () => {
      document.body.classList.remove('story-open');
    });

    const sync = (swiper, announce = true) => {
      const activeIndex = swiper.realIndex;
      const activeCopyIndex = copies.length ? activeIndex % copies.length : 0;

      copies.forEach((copy, index) => {
        const isActive = index === activeCopyIndex;
        copy.classList.toggle('is-active', isActive);
        copy.setAttribute('aria-hidden', String(!isActive));
      });

      if (announce && status) {
        const title = copies[activeCopyIndex]
          ?.querySelector('h3')
          ?.textContent?.trim();
        status.textContent = `Story ${activeIndex + 1} of ${slides.length}${title ? `: ${title}` : ''}`;
      }
    };

    const swiper = new SwiperConstructor(viewport, {
      slidesPerView: 'auto',
      centeredSlides: false,
      touchEventsTarget: 'container',
      spaceBetween: 16,
      maxBackfaceHiddenSlides: 0,
      speed: reducedMotion ? 0 : 850,
      grabCursor: true,
      allowTouchMove: slides.length > 1,
      followFinger: true,
      threshold: 3,
      longSwipesRatio: 0.2,
      longSwipesMs: 250,
      resistance: true,
      resistanceRatio: 0.82,
      touchRatio: 0.95,
      loop: true,
      freeMode: {
        enabled: false,
      },
      navigation: {
        prevEl: previous,
        nextEl: next,
      },
      scrollbar: scrollbar
        ? {
            el: scrollbar,
            dragClass: 'slider-progress__bar',
            draggable: true,
            snapOnRelease: true,
            lockClass: 'is-disabled',
            horizontalClass: 'slider-progress--horizontal',
            scrollbarDisabledClass: 'slider-progress--disabled',
          }
        : undefined,
      breakpoints: {
        1001: {
          centeredSlides: true,
          spaceBetween: 0,
          freeMode: {
            enabled: false,
          },
        },
      },
      a11y: {
        containerMessage: viewport.getAttribute('aria-label'),
      },
      on: {
        init(instance) {
          sync(instance, false);
        },
        realIndexChange(instance) {
          sync(instance);
          if (instance.initialized) {
            setSliderProgressActive(slider, true, true);
          }
        },
        sliderFirstMove() {
          setSliderDragging(slider, true);
        },
        touchEnd() {
          setSliderDragging(slider, false);
        },
        scrollbarDragStart() {
          setSliderDragging(slider, true);
        },
        scrollbarDragEnd() {
          setSliderDragging(slider, false);
        },
      },
    });

    viewport.addEventListener('keydown', (event) => {
      if (event.target !== viewport) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        swiper.slidePrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        swiper.slideNext();
      } else if (event.key === 'Home') {
        event.preventDefault();
        swiper.slideTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        swiper.slideTo(slides.length - 1);
      } else {
        return;
      }
      setSliderProgressActive(slider, true, true);
    });
  });
}

export function initProductCards() {
  document.querySelectorAll('[data-product-card]').forEach((card) => {
    if (card.dataset.initialized === 'true') return;
    card.dataset.initialized = 'true';

    const title = card.dataset.productTitle || 'Product';
    const soldOut = card.dataset.productSoldOut === 'true';
    const priceOverride = card.dataset.productPriceOverride === 'true';
    const linkOverride = card.dataset.productLinkOverride === 'true';
    const imageOverride = card.dataset.productImageOverride === 'true';
    const hoverImageOverride =
      card.dataset.productHoverImageOverride === 'true';
    const quickAdd = card.querySelector('[data-quick-add]');
    const status = card.querySelector('[data-product-status]');
    const variantSelect = card.querySelector('[data-product-variant-select]');
    const optionButtons = [...card.querySelectorAll('[data-product-option]')];
    const currency = card.dataset.productCurrency || 'USD';
    const price = card.querySelector('.product-card__price');
    const currentPrice = card.querySelector(
      '[data-product-card-current-price]',
    );
    const comparePrice = card.querySelector(
      '[data-product-card-compare-price]',
    );
    const badges = card.querySelector('[data-product-card-badges]');
    const discountBadge = card.querySelector('[data-product-card-discount]');
    const soldOutBadge = card.querySelector('[data-product-card-sold-out]');
    const primaryImage = card.querySelector('.product-card__primary-image');
    const hoverImage = card.querySelector('.product-card__hover-image');
    const productLinks = [...card.querySelectorAll('[data-product-card-link]')];

    const selectedOptionMap = () =>
      optionButtons
        .filter((option) => option.getAttribute('aria-pressed') === 'true')
        .filter((option) => option.dataset.optionValue)
        .reduce((selection, option) => {
          selection[Number(option.dataset.optionPosition)] =
            option.dataset.optionValue;
          return selection;
        }, {});

    const selectedOptions = () =>
      Object.entries(selectedOptionMap())
        .sort(([left], [right]) => Number(left) - Number(right))
        .map(([, value]) => value);

    const variantOptions = (option) => {
      try {
        return JSON.parse(option.dataset.options || '[]');
      } catch {
        return [];
      }
    };

    const variants = [...(variantSelect?.options || [])].map((option) => ({
      element: option,
      id: option.value,
      options: variantOptions(option),
      available: option.dataset.available !== 'false',
      price: Number(option.dataset.price) || 0,
      compareAt: Number(option.dataset.compareAt) || 0,
      image: option.dataset.image || '',
      imageAlt: option.dataset.imageAlt || title,
      hoverImage: option.dataset.hoverImage || '',
      hoverImageAlt: option.dataset.hoverImageAlt || '',
      url: option.dataset.url || card.dataset.productCardUrl || '#',
    }));

    const resolveVariant = () => {
      const selection = selectedOptionMap();
      const selectedEntries = Object.entries(selection);
      const matchingVariant = variants.find((variant) =>
        selectedEntries.every(
          ([position, value]) =>
            variant.options[Number(position) - 1] === value,
        ),
      );

      if (selectedEntries.length) return matchingVariant;
      return (
        variants.find((variant) => variant.element.selected) || variants[0]
      );
    };

    const colorPositions = new Set(
      optionButtons
        .filter((option) => option.classList.contains('product-card__color'))
        .map((option) => Number(option.dataset.optionPosition)),
    );

    const syncOptionAvailability = () => {
      const selection = selectedOptionMap();

      optionButtons.forEach((option) => {
        const position = Number(option.dataset.optionPosition);
        const value = option.dataset.optionValue;
        const isColor = option.classList.contains('product-card__color');
        const available = variants.some((variant) => {
          if (!variant.available || variant.options[position - 1] !== value) {
            return false;
          }

          if (isColor) return true;

          return Object.entries(selection).every(
            ([selectedPosition, selectedValue]) => {
              const numericPosition = Number(selectedPosition);
              if (numericPosition === position) return true;
              if (
                colorPositions.has(numericPosition) ||
                numericPosition < position
              ) {
                return variant.options[numericPosition - 1] === selectedValue;
              }
              return true;
            },
          );
        });
        const baseLabel =
          option.dataset.baseAriaLabel ||
          option.getAttribute('aria-label') ||
          value ||
          'Option';

        option.dataset.baseAriaLabel = baseLabel;
        option.dataset.optionAvailable = String(available);
        option.classList.toggle('is-unavailable', !available);
        // Sold-out options remain actionable so customers can inspect every
        // variant. Stock only controls the unavailable styling and quick add.
        option.setAttribute('aria-disabled', 'false');
        option.setAttribute(
          'aria-label',
          available ? baseLabel : `${baseLabel}, sold out`,
        );
      });
    };

    const syncCardState = ({ updateMedia = false } = {}) => {
      syncOptionAvailability();
      const selection = selectedOptions();
      const optionCopy = selection.length ? `, ${selection.join(', ')}` : '';
      const variant = resolveVariant();
      const available = !soldOut && Boolean(variant) && variant.available;
      const variantSoldOut = soldOut || (Boolean(variant) && !available);

      if (variantSelect && variant) variantSelect.value = variant.id;
      if (quickAdd) {
        quickAdd.dataset.variantId = variant?.id || '';
        quickAdd.disabled = !available;
        quickAdd.setAttribute(
          'aria-label',
          available
            ? `Add ${title}${optionCopy} to cart`
            : `${title}${optionCopy} is sold out`,
        );
      }

      card.classList.toggle('product-card--sold-out', variantSoldOut);
      card.dataset.productSoldOut = String(variantSoldOut);

      if (!variant) return;

      const hasDiscount =
        !priceOverride &&
        variant.compareAt > variant.price &&
        variant.compareAt > 0;

      if (currentPrice && !priceOverride) {
        currentPrice.textContent = formatProductCardMoney(
          variant.price,
          currency,
        );
      }
      if (comparePrice) {
        comparePrice.hidden = !hasDiscount;
        comparePrice.textContent = hasDiscount
          ? formatProductCardMoney(variant.compareAt, currency)
          : '';
      }
      price?.classList.toggle('product-card__price--sale', hasDiscount);
      card.classList.toggle('product-card--sale', hasDiscount);

      if (discountBadge) {
        const discount = hasDiscount
          ? Math.round(
              ((variant.compareAt - variant.price) / variant.compareAt) * 100,
            )
          : 0;
        discountBadge.hidden = !hasDiscount;
        discountBadge.textContent = hasDiscount ? `${discount}% off` : '';
      }
      if (soldOutBadge) soldOutBadge.hidden = !variantSoldOut;
      if (badges) {
        badges.hidden = !hasDiscount && !variantSoldOut;
      }

      if (!priceOverride) {
        card.dataset.productPrice = String(variant.price);
        card.dataset.productCompareAt = hasDiscount
          ? String(variant.compareAt)
          : '';
      }
      if (!linkOverride) {
        card.dataset.productCardUrl = variant.url;
        card.dataset.variantId = variant.id;
        productLinks.forEach((link) => {
          link.setAttribute('href', variant.url);
          link.dataset.href = variant.url;
          link.dataset.productUrl = variant.url;
          link.dataset.variantId = variant.id;
        });
      }

      if (updateMedia) {
        if (primaryImage && variant.image && !imageOverride) {
          primaryImage.removeAttribute('srcset');
          primaryImage.src = variant.image;
          primaryImage.alt = variant.imageAlt;
          card.dataset.productImage = variant.image;
          card.classList.add('product-card--variant-selected');
        }
        if (hoverImage && !hoverImageOverride) {
          const hasDistinctHover =
            Boolean(variant.hoverImage) && variant.hoverImage !== variant.image;
          hoverImage.hidden = !hasDistinctHover;
          hoverImage.removeAttribute('srcset');
          if (hasDistinctHover) {
            hoverImage.src = variant.hoverImage;
            hoverImage.alt = variant.hoverImageAlt;
          } else {
            hoverImage.removeAttribute('src');
            hoverImage.alt = '';
          }
        }
      }
    };

    optionButtons.forEach((option) => {
      option.addEventListener('click', () => {
        if (option.disabled) return;
        const position = option.dataset.optionPosition;

        card.querySelectorAll('[data-product-option]').forEach((candidate) => {
          if (candidate.dataset.optionPosition === position) {
            candidate.setAttribute(
              'aria-pressed',
              String(candidate === option),
            );
          }
        });

        quickAdd?.classList.remove('is-added');
        syncCardState({ updateMedia: true });
      });
    });

    quickAdd?.addEventListener('click', async () => {
      if (soldOut || quickAdd.disabled) return;
      const selection = selectedOptions();
      const optionCopy = selection.length ? ` (${selection.join(', ')})` : '';
      const variantId = quickAdd.dataset.variantId || variantSelect?.value;
      if (!variantId) return;

      quickAdd.disabled = true;
      quickAdd.setAttribute('aria-busy', 'true');
      if (status) status.textContent = `Adding ${title}${optionCopy} to cart.`;

      try {
        await addShopifyCartItem(variantId, 1);
        quickAdd.classList.add('is-added');
        if (status) status.textContent = `${title}${optionCopy} added to cart.`;
      } catch (error) {
        quickAdd.classList.remove('is-added');
        if (status) {
          status.textContent =
            error instanceof Error
              ? error.message
              : `${title} could not be added to cart.`;
        }
      } finally {
        quickAdd.removeAttribute('aria-busy');
        syncCardState();
      }
    });

    // Resolve the initial variant media too, so a card hover starts with that
    // variant's second image and falls back to the general product media.
    syncCardState({ updateMedia: true });
  });
}

export function initFavoriteCardActions() {
  if (document.documentElement.dataset.flbFavoriteCardActions === 'true') return;
  document.documentElement.dataset.flbFavoriteCardActions = 'true';

  const getFavoriteAddContext = (event) => {
    const path = event.composedPath?.() || [event.target];
    const card = path.find(
      (node) => node instanceof Element && node.matches('[data-favorite-card]'),
    );
    const swishCard = path.find(
      (node) => node instanceof Element && node.matches('wishlist-product-card'),
    );
    const target =
      event.submitter instanceof HTMLButtonElement
        ? event.submitter
        : path.find(
            (node) =>
              node instanceof HTMLButtonElement &&
              (node.matches('[data-favorite-add]') ||
                (swishCard &&
                  node.matches('button[type="submit"], button.button--primary'))),
          );

    if (!target || (!card && !swishCard)) return null;

    const form =
      path.find(
        (node) => node instanceof HTMLFormElement && node.matches('.wk-form'),
      ) || target.form;
    const variantId =
      target.dataset.variantId ||
      card?.dataset.favoriteVariantId ||
      form?.elements?.id?.value ||
      swishCard?.wishlistItem?.variantId;

    if (!variantId) return null;

    return {
      card: card || swishCard,
      form,
      swishCard,
      target,
      variantId,
    };
  };

  const handleFavoriteAdd = async (event) => {
    const context = getFavoriteAddContext(event);
    if (!context || context.target.disabled) return;

    const { card, form, target, variantId } = context;
    if (target.dataset.flbFavoriteAddBusy === 'true') return;

    event.preventDefault();
    event.stopPropagation();
    target.dataset.flbFavoriteAddBusy = 'true';

    const status =
      form?.querySelector('[data-flb-wishlist-add-status]') ||
      card?.querySelector('[data-favorite-add-status]');
    const title =
      card?.dataset.favoriteTitle ||
      context.swishCard?.wishlistItem?.product?.title ||
      'Product';
    const originalLabel = target.textContent?.trim();
    target.disabled = true;
    target.textContent = 'Adding...';
    if (status) status.textContent = `Adding ${title} to cart.`;

    try {
      await addShopifyCartItem(variantId, 1);
      target.textContent = 'Added to cart';
      if (status) status.textContent = `${title} added to cart.`;
    } catch (error) {
      target.textContent = originalLabel || 'Add to cart';
      if (status) {
        status.textContent =
          error?.message || `${title} could not be added to cart.`;
      }
    } finally {
      window.setTimeout(() => {
        if (!target.isConnected) return;
        target.disabled = false;
        target.removeAttribute('aria-busy');
        target.removeAttribute('data-flb-favorite-add-busy');
        target.textContent = originalLabel || 'Add to cart';
      }, 1400);
    }
  };

  document.addEventListener('click', handleFavoriteAdd, true);
  document.addEventListener('submit', handleFavoriteAdd, true);
}

function initWishlistTranslationFallbacks() {
  document.querySelectorAll('wishlist-page').forEach((wishlistPage) => {
    if (wishlistPage.dataset.flbTranslationFallback === 'true') return;
    wishlistPage.dataset.flbTranslationFallback = 'true';

    let shadowRoot = null;
    let observer = null;
    const patchMissingLabels = () => {
      const nextShadowRoot = wishlistPage.shadowRoot;
      if (!nextShadowRoot) return;

      if (nextShadowRoot !== shadowRoot) {
        observer?.disconnect();
        shadowRoot = nextShadowRoot;
        observer = new MutationObserver(patchMissingLabels);
        observer.observe(shadowRoot, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      shadowRoot.querySelectorAll('button').forEach((button) => {
        const label = button.textContent?.replace(/\s+/g, ' ').trim().toLowerCase();
        if (label?.includes('translation missing: wishlist_product.in_cart')) {
          button.textContent = 'In cart';
        }
      });
    };

    patchMissingLabels();
    const syncTimer = window.setInterval(() => {
      if (!document.documentElement.contains(wishlistPage)) {
        window.clearInterval(syncTimer);
        observer?.disconnect();
        return;
      }
      patchMissingLabels();
    }, 250);

    window.addEventListener('pagehide', () => {
      window.clearInterval(syncTimer);
      observer?.disconnect();
    }, {once: true});
  });
}

function observeDynamicProductCards() {
  const main = document.querySelector('#MainContent');
  if (
    !main ||
    main.dataset.flbProductCardObserver === 'true' ||
    !('MutationObserver' in window)
  ) {
    return;
  }

  main.dataset.flbProductCardObserver = 'true';

  new MutationObserver((mutations) => {
    const hasNewCards = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) =>
          node instanceof Element &&
          (node.matches('[data-product-card]') ||
            node.querySelector('[data-product-card]')),
      ),
    );

    if (hasNewCards) initProductCards();
  }).observe(main, { childList: true, subtree: true });
}

function initCatalogFilterOptionLimits(section) {
  const optionContainers = [
    ...section.querySelectorAll('[data-filter-options]'),
  ];
  if (!optionContainers.length) return () => {};

  const catalogSectionId =
    section.dataset.sectionId ||
    section.closest('[data-section-id]')?.dataset.sectionId ||
    'catalog';
  const states = optionContainers
    .map((options, index) => {
      const button = options.nextElementSibling;
      if (!button?.matches('[data-filter-show-more]')) return null;

      if (!options.id) {
        options.id = `${catalogSectionId}-filter-options-${index + 1}`;
      }
      button.setAttribute('aria-controls', options.id);

      return {
        options,
        button,
        details: options.closest('.collection-filter'),
      };
    })
    .filter(Boolean);

  const syncState = ({ options, button, details }) => {
    if (details && !details.open) return;

    const items = [...options.children].filter((item) => {
      const bounds = item.getBoundingClientRect();
      return bounds.width > 0 && bounds.height > 0;
    });
    const containerBounds = options.getBoundingClientRect();
    const rows = [];

    items.forEach((item) => {
      const bounds = item.getBoundingClientRect();
      const top = Math.round(bounds.top - containerBounds.top);
      const bottom = bounds.bottom - containerBounds.top;
      let row = rows.find((candidate) => Math.abs(candidate.top - top) <= 2);

      if (!row) {
        row = { top, bottom, items: [] };
        rows.push(row);
      }
      row.bottom = Math.max(row.bottom, bottom);
      row.items.push(item);
    });

    rows.sort((left, right) => left.top - right.top);
    const hasOverflow = rows.length > 3;
    button.hidden = !hasOverflow;
    details?.classList.toggle('has-filter-overflow', hasOverflow);

    if (!hasOverflow) {
      button.setAttribute('aria-expanded', 'false');
      button.textContent = 'Show More';
      options.classList.remove('is-collapsed');
      options.style.removeProperty('--filter-collapsed-height');
      items.forEach((item) => {
        item.inert = false;
        item.removeAttribute('aria-hidden');
      });
      return;
    }

    const expanded = button.getAttribute('aria-expanded') === 'true';
    const thirdRow = rows[2];
    options.style.setProperty(
      '--filter-collapsed-height',
      `${Math.ceil(thirdRow.bottom)}px`,
    );
    options.classList.toggle('is-collapsed', !expanded);
    button.textContent = expanded ? 'Show Less' : 'Show More';

    rows.forEach((row, rowIndex) => {
      row.items.forEach((item) => {
        const concealed = !expanded && rowIndex > 2;
        item.inert = concealed;
        if (concealed) item.setAttribute('aria-hidden', 'true');
        else item.removeAttribute('aria-hidden');
      });
    });
  };

  let syncFrame = 0;
  const sync = () => {
    if (syncFrame) window.cancelAnimationFrame(syncFrame);
    syncFrame = window.requestAnimationFrame(() => {
      syncFrame = 0;
      states.forEach(syncState);
    });
  };

  states.forEach((state) => {
    state.button.addEventListener('click', () => {
      const expanded = state.button.getAttribute('aria-expanded') === 'true';
      state.button.setAttribute('aria-expanded', String(!expanded));
      syncState(state);
    });
    state.details?.addEventListener('toggle', () => {
      if (state.details.open) sync();
    });
  });

  window.addEventListener('resize', sync, { passive: true });
  sync();
  return sync;
}

export function initCatalogFilters() {
  document.querySelectorAll('[data-flb-catalog]').forEach((section) => {
    if (section.dataset.catalogInitialized === 'true') return;
    section.dataset.catalogInitialized = 'true';
    section.closest('main')?.classList.add('main--collection');

    const filters = section.querySelector('[data-catalog-filters]');
    const form = section.querySelector('[data-catalog-filter-form]');
    const toggle = section.querySelector('[data-filter-toggle]');
    const toggleLabel = section.querySelector('[data-filter-toggle-label]');
    const sort = section.querySelector('[data-catalog-sort]');
    const mobileMedia = window.matchMedia('(max-width: 1000px)');
    const header = document.querySelector('.site-header');
    let offsetFrame = 0;

    const serverTagFilters = form
      ? [...form.querySelectorAll('[data-server-tag-filter]')]
      : [];
    const appliedSelections = {
      collection: new Set(
        new URL(window.location.href).searchParams.getAll('flb_collection'),
      ),
    };
    const hasSelectionMarkers = appliedSelections.collection.size > 0;

    if (hasSelectionMarkers) {
      serverTagFilters.forEach((input) => {
        const selections = appliedSelections[input.dataset.filterGroup];
        input.checked = selections?.has(input.value) || false;
      });
    } else {
      const restoredTags = new Set();
      serverTagFilters.forEach((input) => {
        if (!input.checked) return;
        const tag = input.dataset.filterTag?.trim().toLowerCase();
        if (!tag || restoredTags.has(tag)) {
          input.checked = false;
          return;
        }
        restoredTags.add(tag);
      });
    }

    const syncFilterOptionLimits = initCatalogFilterOptionLimits(section);

    const syncOffset = () => {
      offsetFrame = 0;
      const headerBottom = header
        ? Math.max(
            0,
            Math.min(window.innerHeight, header.getBoundingClientRect().bottom),
          )
        : 0;
      section.style.setProperty(
        '--collection-sticky-offset',
        `${Math.round(headerBottom)}px`,
      );
    };

    const scheduleOffsetSync = () => {
      if (offsetFrame) return;
      offsetFrame = requestAnimationFrame(syncOffset);
    };

    const setFiltersOpen = (open) => {
      if (!filters || !toggle) return;
      section.classList.toggle('collection-catalog--filters-hidden', !open);
      toggle.setAttribute('aria-expanded', String(open));
      if (toggleLabel) {
        toggleLabel.textContent = open ? 'Hide filters' : 'Show filters';
      }
      filters.setAttribute('aria-hidden', String(!open));
      filters.inert = !open;
      document.body.classList.toggle(
        'filters-open',
        open && mobileMedia.matches,
      );
      if (open) syncFilterOptionLimits();
    };

    section.querySelectorAll('[data-catalog-price-range]').forEach((range) => {
      const minimum = range.querySelector('[data-price-min]');
      const maximum = range.querySelector('[data-price-max]');
      const minimumOutput = range.querySelector('[data-price-min-output]');
      const maximumOutput = range.querySelector('[data-price-max-output]');
      const track = range.querySelector('[data-price-track]');
      const prefix = range.dataset.moneyPrefix || '$';

      if (!minimum || !maximum) return;

      const updateRange = (changedInput) => {
        const step = Number(minimum.step) || 1;
        if (
          changedInput === minimum &&
          Number(minimum.value) > Number(maximum.value) - step
        ) {
          minimum.value = String(
            Math.max(Number(minimum.min), Number(maximum.value) - step),
          );
        }
        if (
          changedInput === maximum &&
          Number(maximum.value) < Number(minimum.value) + step
        ) {
          maximum.value = String(
            Math.min(Number(maximum.max), Number(minimum.value) + step),
          );
        }

        const lowerBound = Number(minimum.min);
        const upperBound = Number(maximum.max);
        const availableRange = Math.max(upperBound - lowerBound, 1);
        const start =
          ((Number(minimum.value) - lowerBound) / availableRange) * 100;
        const end =
          ((Number(maximum.value) - lowerBound) / availableRange) * 100;

        track?.style.setProperty('--price-start', `${start}%`);
        track?.style.setProperty('--price-end', `${100 - end}%`);
        if (minimumOutput)
          minimumOutput.textContent = `${prefix}${minimum.value}`;
        if (maximumOutput)
          maximumOutput.textContent = `${prefix}${maximum.value}`;
      };

      minimum.addEventListener('input', () => updateRange(minimum));
      maximum.addEventListener('input', () => updateRange(maximum));
      updateRange();
    });

    toggle?.addEventListener('click', () => {
      setFiltersOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });

    section.querySelectorAll('[data-filter-close]').forEach((button) => {
      button.addEventListener('click', () => setFiltersOpen(false));
    });

    form?.addEventListener('submit', () => {
      const tagHandles = [
        ...new Set(
          [...form.querySelectorAll('[data-server-tag-filter]:checked')]
            .map((input) =>
              (input.dataset.filterTag || input.value)
                .trim()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .replace(/['\u2019]/g, '')
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, ''),
            )
            .filter(Boolean),
        ),
      ];

      const baseAction = form.getAttribute('action')?.replace(/\/$/, '') || '';
      form.action = tagHandles.length
        ? `${baseAction}/${tagHandles.join('+')}`
        : baseAction;

      form.querySelectorAll('[data-catalog-price-range]').forEach((range) => {
        const minimum = range.querySelector('[data-price-min]');
        const maximum = range.querySelector('[data-price-max]');
        if (minimum?.value === minimum?.min) minimum.disabled = true;
        if (maximum?.value === maximum?.max) maximum.disabled = true;
      });
    });

    sort?.querySelectorAll('[data-sort-value]').forEach((button) => {
      button.addEventListener('click', () => {
        const value = button.dataset.sortValue;
        if (!value) return;

        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', value);
        url.searchParams.delete('page');
        window.location.assign(url);
      });
    });

    document.addEventListener('click', (event) => {
      if (sort?.open && !sort.contains(event.target)) sort.open = false;
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      sort?.removeAttribute('open');
      if (mobileMedia.matches) setFiltersOpen(false);
    });

    mobileMedia.addEventListener?.('change', (event) => {
      syncOffset();
      setFiltersOpen(!event.matches);
    });
    window.addEventListener('resize', scheduleOffsetSync, { passive: true });
    window.addEventListener('scroll', scheduleOffsetSync, { passive: true });

    syncOffset();
    setFiltersOpen(!mobileMedia.matches);
  });
}

export function initProductDetail() {
  document.querySelectorAll('[data-product-detail]').forEach((section) => {
    section
      .querySelectorAll('.product-detail__accordion > .rte')
      .forEach((content) => {
        content
          .querySelectorAll('style, link[rel~="stylesheet"]')
          .forEach((element) => element.remove());

        content.querySelectorAll('[style], [class]').forEach((element) => {
          element.removeAttribute('style');
          element.removeAttribute('class');
        });
      });

    if (section.dataset.productDetailInitialized === 'true') return;
    section.dataset.productDetailInitialized = 'true';
    section.closest('main')?.classList.add('main--product');

    const variantData = section.querySelector('[data-product-variants]');
    let variants = [];
    try {
      variants = JSON.parse(variantData?.textContent || '[]');
    } catch {
      variants = [];
    }

    const form = section.querySelector('[data-product-form]');
    const swishProductButton = section.querySelector('[data-swish-product-page-button]');
    const swishProductLink = section.querySelector('[data-swish-product-link]');
    const variantInput = form?.querySelector('[data-product-variant-id]');
    const addButton = form?.querySelector('[data-product-add-button]');
    const mobileBar = section.querySelector('[data-detail-mobile-bar]');
    const mobileAdd = section.querySelector('[data-detail-mobile-add]');
    const quantityInput = form?.querySelector('[data-detail-quantity-input]');
    const quantityValue = form?.querySelector('[data-detail-quantity-value]');
    const price = section.querySelector('[data-product-detail-price]');
    const currentPrice = section.querySelector('[data-product-current-price]');
    const comparePrice = section.querySelector('[data-product-compare-price]');
    const galleryBadges = section.querySelector(
      '[data-product-gallery-badges]',
    );
    const discountBadge = section.querySelector(
      '[data-product-discount-badge]',
    );
    const soldOutBadge = section.querySelector('[data-product-sold-out-badge]');
    const galleryMain = section.querySelector('.product-gallery__main');
    const galleryThumbs = section.querySelector('.product-gallery__thumbs');
    const galleryProgress = section.querySelector(
      '[data-product-gallery-progress]',
    );
    const galleryNav = section.querySelector('[data-product-gallery-nav]');
    const media = [...section.querySelectorAll('[data-gallery-media]')];
    const thumbs = [...section.querySelectorAll('[data-gallery-thumb]')];
    const dots = [...section.querySelectorAll('[data-gallery-dot]')];
    const firstElementByKey = (elements) =>
      elements.reduce((map, element) => {
        const key = element.dataset.galleryKey;
        if (key && !map.has(key)) map.set(key, element);
        return map;
      }, new Map());
    const thumbByKey = firstElementByKey(thumbs);
    const dotByKey = firstElementByKey(dots);
    const galleryEntries = [];
    const galleryEntryByKey = new Map();
    media.forEach((item) => {
      const key = item.dataset.galleryKey;
      if (!key || galleryEntryByKey.has(key)) {
        item.hidden = true;
        return;
      }
      const entry = {
        key,
        media: item,
        thumb: thumbByKey.get(key),
        dot: dotByKey.get(key),
      };
      galleryEntries.push(entry);
      galleryEntryByKey.set(key, entry);
    });
    galleryEntries.forEach((entry) => {
      entry.media.hidden = false;
      entry.media.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager';
        if (typeof image.decode === 'function') {
          image.decode().catch(() => {});
        }
      });
    });
    thumbs.forEach((thumb) => {
      if (galleryEntryByKey.get(thumb.dataset.galleryKey)?.thumb !== thumb) {
        thumb.hidden = true;
      }
    });
    dots.forEach((dot) => {
      if (galleryEntryByKey.get(dot.dataset.galleryKey)?.dot !== dot) {
        dot.hidden = true;
      }
    });
    const assignedMediaKeys = new Set(
      variants.flatMap((variant) => variant.assignedMediaKeys || []),
    );
    const header = document.querySelector('.site-header');
    const footer = document.querySelector('.site-footer');
    let activeEntries = [];
    let activeMedia = 0;
    let pointerStart = null;
    let stickyFrame = 0;
    let lastSwishVariantId = '';

    const syncSwishProductLink = (variantId) => {
      if (!variantId || !swishProductLink) return;

      const url = new URL(
        swishProductLink.href || window.location.href,
        window.location.origin,
      );
      url.searchParams.set('variant', variantId);
      const productUrl = `${url.pathname}${url.search}${url.hash}`;
      swishProductLink.href = productUrl;
      swishProductLink.dataset.href = productUrl;
      swishProductLink.dataset.productUrl = productUrl;
      swishProductLink.dataset.variantId = String(variantId);
      if (swishProductButton) {
        swishProductButton.dataset.variantId = String(variantId);
      }
    };

    const syncSwishVariantFromCurrentSelection = () => {
      const urlVariantId = new URL(window.location.href).searchParams.get('variant');
      const variantId = urlVariantId || variantInput?.value || '';
      if (!variantId || variantId === lastSwishVariantId) return;
      lastSwishVariantId = String(variantId);
      syncSwishProductLink(variantId);
    };

    form?.addEventListener('change', syncSwishVariantFromCurrentSelection, true);
    form?.addEventListener('input', syncSwishVariantFromCurrentSelection, true);
    swishProductButton?.addEventListener('click', syncSwishVariantFromCurrentSelection, true);
    swishProductLink?.addEventListener('click', syncSwishVariantFromCurrentSelection, true);

    const swishVariantSyncTimer = window.setInterval(() => {
      if (!section.isConnected) {
        window.clearInterval(swishVariantSyncTimer);
        return;
      }
      syncSwishVariantFromCurrentSelection();
    }, 250);

    window.addEventListener('pagehide', () => {
      window.clearInterval(swishVariantSyncTimer);
    }, { once: true });

    const selectMedia = (index) => {
      if (!activeEntries.length) return;
      const next =
        (Number(index) + activeEntries.length) % activeEntries.length;
      activeMedia = next;
      const activeEntry = activeEntries[next];
      galleryEntries.forEach((entry) => {
        const active = entry === activeEntry;
        entry.media.classList.toggle('is-active', active);
        entry.media.setAttribute('aria-hidden', String(!active));
        if (!active) {
          entry.media
            .querySelectorAll('video')
            .forEach((video) => video.pause());
        }
        entry.thumb?.setAttribute('aria-pressed', String(active));
        entry.dot?.setAttribute('aria-pressed', String(active));
      });
    };

    const syncGallery = (variant) => {
      const orderedKeys = [
        variant?.primaryMediaKey,
        ...(variant?.secondaryMediaKeys || []),
      ].filter(Boolean);
      const seenKeys = new Set();
      activeEntries = [];

      orderedKeys.forEach((key) => {
        const entry = galleryEntryByKey.get(key);
        if (!entry || seenKeys.has(key)) return;
        seenKeys.add(key);
        activeEntries.push(entry);
      });
      galleryEntries.forEach((entry) => {
        if (assignedMediaKeys.has(entry.key) || seenKeys.has(entry.key)) return;
        seenKeys.add(entry.key);
        activeEntries.push(entry);
      });

      const activeEntrySet = new Set(activeEntries);
      galleryEntries.forEach((entry) => {
        const eligible = activeEntrySet.has(entry);
        if (entry.thumb) entry.thumb.hidden = !eligible;
        if (entry.dot) entry.dot.hidden = !eligible;
      });
      activeEntries.forEach((entry, index) => {
        entry.thumb?.setAttribute(
          'aria-label',
          `View product media ${index + 1}`,
        );
        entry.thumb?.setAttribute('data-gallery-index', String(index));
        entry.dot?.setAttribute(
          'aria-label',
          `View product media ${index + 1}`,
        );
        entry.dot?.setAttribute('data-gallery-index', String(index));
        if (entry.thumb && galleryThumbs) galleryThumbs.append(entry.thumb);
        if (entry.dot && galleryProgress) galleryProgress.append(entry.dot);
      });
      if (galleryNav) galleryNav.hidden = activeEntries.length <= 1;
      if (galleryProgress) galleryProgress.hidden = activeEntries.length <= 1;
      selectMedia(0);
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const entry = galleryEntryByKey.get(thumb.dataset.galleryKey);
        selectMedia(activeEntries.indexOf(entry));
      });
    });
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const entry = galleryEntryByKey.get(dot.dataset.galleryKey);
        selectMedia(activeEntries.indexOf(entry));
      });
    });
    section.querySelectorAll('[data-gallery-direction]').forEach((button) => {
      button.addEventListener('click', () => {
        selectMedia(activeMedia + Number(button.dataset.galleryDirection));
      });
    });

    galleryMain?.addEventListener('dragstart', (event) =>
      event.preventDefault(),
    );
    galleryMain?.addEventListener('pointerdown', (event) => {
      if (
        !event.isPrimary ||
        event.button !== 0 ||
        (event.target instanceof Element &&
          event.target.closest('button, video, iframe, model-viewer'))
      ) {
        return;
      }
      pointerStart = {
        x: event.clientX,
        y: event.clientY,
        id: event.pointerId,
      };
    });
    galleryMain?.addEventListener('pointerup', (event) => {
      if (!pointerStart || pointerStart.id !== event.pointerId) return;
      const x = event.clientX - pointerStart.x;
      const y = event.clientY - pointerStart.y;
      pointerStart = null;
      if (Math.abs(x) > 36 && Math.abs(x) > Math.abs(y)) {
        selectMedia(activeMedia + (x < 0 ? 1 : -1));
      }
    });
    galleryMain?.addEventListener('pointercancel', () => {
      pointerStart = null;
    });

    const optionButtons = [...section.querySelectorAll('[data-detail-option]')];
    const selectedOptions = () => {
      const selection = [];
      optionButtons.forEach((button) => {
        if (button.getAttribute('aria-pressed') === 'true') {
          selection[Number(button.dataset.optionPosition) - 1] =
            button.dataset.optionValue;
        }
      });
      return selection;
    };

    const variantForOptions = (options) =>
      variants.find(
        (variant) =>
          variant.options.length === options.length &&
          variant.options.every((value, index) => value === options[index]),
      );

    const syncPressedOptions = (variant) => {
      if (!variant) return;
      optionButtons.forEach((button) => {
        const position = Number(button.dataset.optionPosition) - 1;
        button.setAttribute(
          'aria-pressed',
          String(variant.options[position] === button.dataset.optionValue),
        );
      });
    };

    const syncOptionAvailability = (variant) => {
      const selection = variant?.options || selectedOptions();
      optionButtons.forEach((button) => {
        const position = Number(button.dataset.optionPosition) - 1;
        const possible = variants.some((candidate) => {
          if (!candidate.available) return false;
          return candidate.options.every((value, index) =>
            index === position
              ? value === button.dataset.optionValue
              : !selection[index] || value === selection[index],
          );
        });
        button.classList.toggle('is-unavailable', !possible);
        button.dataset.optionAvailable = String(possible);
      });
    };

    const syncVariant = (variant, { updateUrl = false } = {}) => {
      const available = Boolean(variant?.available);
      if (variantInput) variantInput.value = variant?.id || '';
      if (variant?.id) {
        lastSwishVariantId = String(variant.id);
        syncSwishProductLink(variant.id);
      }
      if (currentPrice)
        currentPrice.textContent = variant?.priceFormatted || '';

      const onSale =
        Boolean(variant) &&
        Number(variant.compareAtPrice) > Number(variant.price);
      if (comparePrice) {
        comparePrice.hidden = !onSale;
        comparePrice.textContent = onSale
          ? variant.compareAtPriceFormatted || ''
          : '';
      }
      price?.classList.toggle('product-detail__price--sale', onSale);
      section.classList.toggle('product-detail--sale', onSale);
      section.classList.toggle('product-detail--sold-out', !available);
      if (discountBadge) {
        discountBadge.hidden = !onSale;
        discountBadge.textContent = onSale
          ? `${variant.discountPercent}% off`
          : '';
      }
      if (soldOutBadge) soldOutBadge.hidden = available;
      if (galleryBadges) galleryBadges.hidden = !onSale && available;

      const label = !variant
        ? 'Unavailable'
        : available
          ? 'Add to cart'
          : 'Sold out';
      if (addButton) {
        addButton.textContent = label;
        addButton.disabled = !available;
        addButton.dataset.variantAvailable = String(available);
      }
      if (mobileAdd) {
        mobileAdd.textContent = label;
        mobileAdd.disabled = !available;
        mobileAdd.dataset.variantAvailable = String(available);
      }

      if (quantityInput && variant?.quantityRule) {
        const minimum = Number(variant.quantityRule.min) || 1;
        const increment = Number(variant.quantityRule.increment) || 1;
        const maximum = Number(variant.quantityRule.max) || null;
        quantityInput.min = String(minimum);
        quantityInput.step = String(increment);
        if (maximum) quantityInput.max = String(maximum);
        else quantityInput.removeAttribute('max');
        let quantity = Math.max(
          minimum,
          Number(quantityInput.value) || minimum,
        );
        if (maximum) quantity = Math.min(maximum, quantity);
        quantityInput.value = String(quantity);
        if (quantityValue) quantityValue.textContent = String(quantity);
      }

      optionButtons.forEach((button) => {
        if (button.getAttribute('aria-pressed') !== 'true') return;
        const labelTarget = section.querySelector(
          `[data-selected-option-label="${button.dataset.optionPosition}"]`,
        );
        if (labelTarget) labelTarget.textContent = button.dataset.optionValue;
      });
      syncOptionAvailability(variant);

      syncGallery(variant);

      if (updateUrl && variant?.id && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };

    optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const position = Number(button.dataset.optionPosition);
        optionButtons.forEach((candidate) => {
          if (Number(candidate.dataset.optionPosition) === position) {
            candidate.setAttribute(
              'aria-pressed',
              String(candidate === button),
            );
          }
        });

        let variant = variantForOptions(selectedOptions());
        if (!variant) {
          variant =
            variants.find(
              (candidate) =>
                candidate.available &&
                candidate.options[position - 1] === button.dataset.optionValue,
            ) ||
            variants.find(
              (candidate) =>
                candidate.options[position - 1] === button.dataset.optionValue,
            );
          syncPressedOptions(variant);
        }
        syncVariant(variant, { updateUrl: true });
      });
    });

    form?.querySelectorAll('[data-detail-quantity]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!quantityInput) return;
        const minimum = Number(quantityInput.min) || 1;
        const maximum = Number(quantityInput.max) || Infinity;
        const increment = Number(quantityInput.step) || 1;
        const direction = Number(button.dataset.detailQuantity) || 0;
        const next = Math.max(
          minimum,
          Math.min(
            maximum,
            Number(quantityInput.value) + direction * increment,
          ),
        );
        quantityInput.value = String(next);
        if (quantityValue) quantityValue.textContent = String(next);
      });
    });

    mobileAdd?.addEventListener('click', () => {
      if (mobileAdd.disabled) {
        section
          .querySelector('.product-detail__option')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      form?.requestSubmit(addButton);
    });

    const syncMobileBar = () => {
      if (!mobileBar || !addButton) return;
      const mobile = window.matchMedia('(max-width: 1000px)').matches;
      const addBounds = addButton.getBoundingClientRect();
      const addVisible =
        addBounds.top < window.innerHeight && addBounds.bottom > 0;
      const footerBounds = footer?.getBoundingClientRect();
      const footerVisible =
        footerBounds &&
        footerBounds.top < window.innerHeight &&
        footerBounds.bottom > 0;
      const visible = mobile && !addVisible && !footerVisible;
      mobileBar.classList.toggle('is-visible', visible);
      mobileBar.setAttribute('aria-hidden', String(!visible));
      mobileBar.inert = !visible;
    };

    const syncStickyOffset = () => {
      stickyFrame = 0;
      const bottom = header
        ? Math.max(
            0,
            Math.min(window.innerHeight, header.getBoundingClientRect().bottom),
          )
        : 0;
      section.style.setProperty(
        '--product-sticky-offset',
        `${Math.round(bottom)}px`,
      );
    };
    const scheduleStickyOffset = () => {
      if (stickyFrame) return;
      stickyFrame = requestAnimationFrame(syncStickyOffset);
    };

    window.addEventListener('scroll', syncMobileBar, { passive: true });
    window.addEventListener('scroll', scheduleStickyOffset, { passive: true });
    window.addEventListener('resize', () => {
      syncMobileBar();
      scheduleStickyOffset();
    });

    const initialVariant = variantForOptions(selectedOptions()) || variants[0];
    syncVariant(initialVariant);
    syncStickyOffset();
    syncMobileBar();
  });
}

export function initProductForms() {
  document.querySelectorAll('[data-product-form]').forEach((form) => {
    if (form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';

    const submit = form.querySelector('[type="submit"][name="add"]');
    const status = form.querySelector('[data-product-form-status]');

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!form.reportValidity() || submit?.disabled) return;

      const data = new FormData(form);
      const variantId = data.get('id');
      const quantity = data.get('quantity') || 1;
      if (!variantId) return;

      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
      }
      if (status) status.textContent = 'Adding product to cart.';

      const originalLabel = submit?.textContent;
      try {
        const result = await addShopifyCartItem(variantId, quantity);
        if (submit) submit.textContent = 'Added to bag';
        if (status) {
          status.textContent = `${result.added?.product_title || result.added?.title || 'Product'} added to cart.`;
        }
      } catch (error) {
        if (status) {
          status.textContent =
            error instanceof Error
              ? error.message
              : 'This product could not be added to cart.';
        }
      } finally {
        if (submit) {
          window.setTimeout(() => {
            submit.textContent = originalLabel || 'Add to cart';
          }, 1400);
          submit.disabled = submit.dataset.variantAvailable === 'false';
          submit.removeAttribute('aria-busy');
        }
      }
    });
  });
}

function targetFromHash(hash) {
  if (!hash || hash === '#') return null;

  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    return null;
  }
}

function visibleStickyHeaderHeight() {
  const header = document.querySelector('.site-header');
  if (!header) return 0;

  const styles = window.getComputedStyle(header);
  if (
    !['sticky', 'fixed'].includes(styles.position) ||
    styles.display === 'none' ||
    styles.visibility === 'hidden'
  ) {
    return 0;
  }

  const bounds = header.getBoundingClientRect();
  const configuredTop = Number.parseFloat(styles.top);
  const stickyTop = Number.isFinite(configuredTop)
    ? configuredTop
    : Math.min(0, bounds.top);

  return Math.max(0, Math.min(window.innerHeight, bounds.height + stickyTop));
}

function hashScrollPosition(target) {
  const rootStyles = window.getComputedStyle(document.documentElement);
  const targetStyles = window.getComputedStyle(target);
  const scrollPadding = Number.parseFloat(rootStyles.scrollPaddingTop) || 0;
  const scrollMargin = Number.parseFloat(targetStyles.scrollMarginTop) || 0;

  return Math.max(
    0,
    target.getBoundingClientRect().top +
      window.scrollY -
      scrollPadding -
      scrollMargin,
  );
}

export function initHashNavigation() {
  const header = document.querySelector('.site-header');

  const syncHeaderOffset = () => {
    document.documentElement.style.setProperty(
      '--sticky-header-visible-height',
      `${Math.ceil(visibleStickyHeaderHeight())}px`,
    );
  };

  const alignCurrentHash = () => {
    const target = targetFromHash(window.location.hash);
    if (target) target.scrollIntoView({ block: 'start' });
  };

  syncHeaderOffset();
  window.addEventListener('resize', syncHeaderOffset);

  if (header && 'ResizeObserver' in window) {
    new ResizeObserver(syncHeaderOffset).observe(header);
  }

  if (header && 'MutationObserver' in window) {
    new MutationObserver(syncHeaderOffset).observe(header, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });
  }

  if (window.location.hash) {
    requestAnimationFrame(() => {
      syncHeaderOffset();
      alignCurrentHash();
    });
  }
}

export function initSmoothScrolling() {
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  if (reducedMotion) return;

  document.documentElement.classList.add('smooth-scroll-enabled');

  let current = window.scrollY;
  let destination = current;
  let animationFrame = 0;
  let previousTime = performance.now();
  const pageScrollIsLocked = () =>
    document.body.classList.contains('menu-open') ||
    document.body.classList.contains('search-open') ||
    document.body.classList.contains('cart-drawer-open');

  const maximumScroll = () =>
    Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const clampScroll = (value) => Math.max(0, Math.min(value, maximumScroll()));

  const stopAnimation = () => {
    if (animationFrame) cancelAnimationFrame(animationFrame);
    animationFrame = 0;
    current = window.scrollY;
    destination = current;
  };

  const animateScroll = (time) => {
    if (pageScrollIsLocked()) {
      stopAnimation();
      return;
    }

    const elapsed = Math.min(Math.max(time - previousTime, 0), 64);
    previousTime = time;
    destination = clampScroll(destination);
    const ease = 1 - Math.exp(-elapsed / 180);
    current += (destination - current) * ease;

    if (Math.abs(destination - current) < 0.35) {
      current = destination;
      window.scrollTo(0, current);
      animationFrame = 0;
      return;
    }

    window.scrollTo(0, current);
    animationFrame = requestAnimationFrame(animateScroll);
  };

  const scrollToPosition = (position) => {
    if (pageScrollIsLocked()) return;
    if (!animationFrame) current = window.scrollY;
    destination = clampScroll(position);
    previousTime = performance.now();
    if (!animationFrame) animationFrame = requestAnimationFrame(animateScroll);
  };

  const canScrollInside = (target, delta) => {
    let element = target instanceof Element ? target : null;

    while (element && element !== document.body) {
      const { overflowY } = getComputedStyle(element);
      const scrollable =
        /(auto|scroll)/.test(overflowY) &&
        element.scrollHeight > element.clientHeight + 1;

      if (scrollable) {
        const canScrollDown =
          delta > 0 &&
          element.scrollTop + element.clientHeight < element.scrollHeight - 1;
        const canScrollUp = delta < 0 && element.scrollTop > 1;
        if (canScrollDown || canScrollUp) return true;
      }

      element = element.parentElement;
    }

    return false;
  };

  window.addEventListener(
    'wheel',
    (event) => {
      const eventTarget = event.target instanceof Element ? event.target : null;
      if (
        event.defaultPrevented ||
        event.ctrlKey ||
        document.body.classList.contains('menu-open') ||
        document.body.classList.contains('search-open') ||
        document.body.classList.contains('cart-drawer-open') ||
        Math.abs(event.deltaX) > Math.abs(event.deltaY) ||
        eventTarget?.closest(
          'input, textarea, select, [data-smooth-scroll-ignore]',
        )
      ) {
        return;
      }

      let delta = event.deltaY;
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) delta *= 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        delta *= window.innerHeight;
      }
      if (!delta || canScrollInside(eventTarget, delta)) return;

      event.preventDefault();
      const limitedDelta = Math.sign(delta) * Math.min(Math.abs(delta), 160);
      scrollToPosition(destination + limitedDelta);
    },
    { passive: false },
  );

  document.addEventListener('click', (event) => {
    const eventTarget = event.target instanceof Element ? event.target : null;
    const link = eventTarget?.closest('a[href]');
    if (!link || link.hasAttribute('download') || link.target === '_blank')
      return;

    const url = new URL(link.href, window.location.href);
    if (
      url.origin !== window.location.origin ||
      url.pathname !== window.location.pathname ||
      url.search !== window.location.search ||
      !url.hash ||
      url.hash === '#'
    ) {
      return;
    }

    const target = targetFromHash(url.hash);
    if (!target) return;

    event.preventDefault();
    scrollToPosition(hashScrollPosition(target));
    if (window.location.hash !== url.hash)
      history.pushState(null, '', url.hash);
  });

  window.addEventListener(
    'scroll',
    () => {
      if (animationFrame) return;
      current = window.scrollY;
      destination = current;
    },
    { passive: true },
  );

  window.addEventListener('resize', () => {
    destination = clampScroll(destination);
  });
  document.addEventListener('flb:scroll-lock', stopAnimation);
  document.addEventListener('flb:scroll-unlock', (event) => {
    current = event.detail?.scrollY ?? window.scrollY;
    destination = current;
  });
  window.addEventListener('touchstart', stopAnimation, { passive: true });
  window.addEventListener('pointerdown', stopAnimation, { passive: true });
  window.addEventListener('keydown', (event) => {
    if (
      [
        'ArrowUp',
        'ArrowDown',
        'PageUp',
        'PageDown',
        'Home',
        'End',
        ' ',
      ].includes(event.key)
    ) {
      stopAnimation();
    }
  });
}

export function initAnimations() {
  const animated = document.querySelectorAll('[data-animate]');
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  if (reducedMotion || !('IntersectionObserver' in window)) {
    animated.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -4% 0px', threshold: 0.08 },
  );

  animated.forEach((element) => observer.observe(element));
}

const KLAVIYO_NEWSLETTER_REVISION = '2026-07-15';

async function getKlaviyoCompanyId() {
  if (typeof window.klaviyo?.account !== 'function') return '';

  try {
    const companyId = await window.klaviyo.account();
    return typeof companyId === 'string' ? companyId.trim() : '';
  } catch (error) {
    console.warn('Klaviyo account could not be resolved.', error);
    return '';
  }
}

function setNewsletterStatus(container, message, { error = false } = {}) {
  const status = container.querySelector('[data-newsletter-status]');
  if (!status) return;
  status.textContent = message;
  status.classList.toggle('is-error', error);
}

function finishNewsletterSubmission(form) {
  delete form.dataset.newsletterSubmitting;
  form.classList.remove('is-loading');
  form.removeAttribute('aria-busy');
  const button = form.querySelector('button[type="submit"]');
  if (button) button.disabled = false;
}

function startNewsletterSubmission(form, container) {
  const status = container.querySelector('[data-newsletter-status]');
  const success = form.querySelector('[data-newsletter-success]');
  const button = form.querySelector('button[type="submit"]');

  form.dataset.newsletterSubmitting = 'true';
  form.classList.add('is-loading');
  form.classList.remove('is-success');
  form.setAttribute('aria-busy', 'true');
  status?.classList.remove('is-error');
  if (status) status.textContent = '';
  if (success) {
    success.hidden = true;
    success.setAttribute('aria-hidden', 'true');
  }
  if (button) button.disabled = true;
}

async function handleNewsletterSubmit(event) {
  event.preventDefault();
  const form = event.currentTarget;
  if (!(form instanceof HTMLFormElement)) return;

  const container = form.closest('[data-newsletter-container]');
  if (!container) return;
  if (form.dataset.newsletterSubmitting === 'true') {
    return;
  }
  if (!form.reportValidity()) return;

  const input = form.querySelector('input[type="email"]');
  const listId = form.dataset.klaviyoListId?.trim();
  const revision =
    form.dataset.klaviyoRevision?.trim() || KLAVIYO_NEWSLETTER_REVISION;
  const source = form.dataset.klaviyoSource?.trim() || 'FLB website newsletter';

  if (!(input instanceof HTMLInputElement) || !listId) {
    setNewsletterStatus(container, 'Newsletter signup is not configured yet.', {
      error: true,
    });
    return;
  }

  startNewsletterSubmission(form, container);

  try {
    const companyId = await getKlaviyoCompanyId();
    if (!companyId) {
      throw new Error('Klaviyo onsite JavaScript is not available.');
    }

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
          revision,
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: source,
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: input.value.trim(),
                    subscriptions: {
                      email: {
                        marketing: { consent: 'SUBSCRIBED' },
                      },
                    },
                  },
                },
              },
            },
            relationships: {
              list: { data: { type: 'list', id: listId } },
            },
          },
        }),
      },
    );

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const detail = payload?.errors?.[0]?.detail;
      throw new Error(detail || `Klaviyo request failed (${response.status}).`);
    }

    const success = form.querySelector('[data-newsletter-success]');
    form.reset();
    form.classList.add('is-success');
    if (success) {
      success.hidden = false;
      success.setAttribute('aria-hidden', 'false');
    }
  } catch (error) {
    console.error('Klaviyo newsletter signup failed.', error);
    setNewsletterStatus(
      container,
      'We could not complete your signup. Please try again.',
      { error: true },
    );
  } finally {
    finishNewsletterSubmission(form);
  }
}

export function initNewsletterForms() {
  document.querySelectorAll('[data-newsletter-form]').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.newsletterInitialized === 'true') return;

    form.dataset.newsletterInitialized = 'true';
    form.addEventListener('submit', handleNewsletterSubmit);
  });
}

export function initFooterAccordions() {
  const media = window.matchMedia('(max-width: 1000px)');
  document.querySelectorAll('.site-footer__columns').forEach((group) => {
    if (group.dataset.footerAccordionsInitialized === 'true') return;
    group.dataset.footerAccordionsInitialized = 'true';

    const columns = [...group.querySelectorAll('.site-footer__column')];
    const sync = () => {
      columns.forEach((details) => {
        details.open = !media.matches;
      });
    };

    columns.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!media.matches || !details.open) return;

        columns.forEach((otherDetails) => {
          if (otherDetails !== details) otherDetails.open = false;
        });
      });
    });

    sync();
    media.addEventListener?.('change', sync);
  });
}

export function initLiveChatAction() {
  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('a[href="#live-chat"]');

    if (!trigger) return;

    event.preventDefault();

    if (typeof window.Intercom === 'function') {
      window.Intercom('show');
    }
  });
}
function initFlbTheme() {
  // This handler must survive failures or section re-renders in other modules.
  initNewsletterForms();
  initFavoriteCardActions();
  initWishlistTranslationFallbacks();
  if (!document.querySelector('[data-section-id], [data-cart-drawer]')) return;

  initCartUi();
  initCartPage();
  initHashNavigation();
  initDirectionalUnderlines();
  initNavigation();
  initSearchForms();
  initAnnouncementSliders();
  initHeroSliders();
  initBenefitsSliders();
  initProductSliderSources();
  // Account pages are rendered from page templates and may not include a
  // section wrapper, but their sliders still need the shared Swiper setup.
  initSwiperSliders();
  initStorySliders();
  initProductCards();
  observeDynamicProductCards();
  initCatalogFilters();
  initProductDetail();
  initProductReviewAccordions();
  initProductForms();
  initAnimations();
  initFooterAccordions();
  initLiveChatAction();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (!(section instanceof Element)) return;
  if (
    !section.matches('[data-slider]') &&
    !section.querySelector('[data-slider]')
  )
    return;

  window.requestAnimationFrame(() => initSwiperSliders());
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFlbTheme, { once: true });
} else {
  initFlbTheme();
}
